/**
 * Подстраницы «готовых инструментов» Bafsy (/:lang/solutions/:slug).
 *
 * Hand-curated мирор `content/10-subproducts/*.md` — ровно как `READY_SOLUTIONS`
 * и `ENGINE_MODULES` в `home-data.ts` мирорят свои markdown-исходники.
 *
 * RU и EN копирайт хранятся бок о бок: RU на базовых полях (`heading`,
 * `lead`, …), EN — на параллельных `*En`-полях. Тон EN — confident,
 * active voice, B2B для FMCG / retail marketing-директоров (см. также
 * `[site/src/i18n/locales/en.ts](src/i18n/locales/en.ts)`).
 *
 * Источник копирайта (RU): `assets/text.txt` (см. также
 * `content/10-subproducts/<slug>.md` — текстовое зеркало).
 */

import type { SubproductItem } from '@/types/component-types';

export const SUBPRODUCTS: readonly SubproductItem[] = [
  /* ------------------------------------------------------------------------ */
  /* 1. Лендинг                                                               */
  /* ------------------------------------------------------------------------ */
  {
    id: 'landing',
    seoTitle: 'Промо-лендинг под ключ',
    seoTitleEn: 'Turnkey promo landing pages',
    heading: 'Промо-лендинг под ключ: собираем страницу акции и запускаем за считанные дни',
    headingEn: 'Turnkey promo landing pages — launched fast, built for conversion',
    lead: 'Одностраничный сайт промо-акции: собираем из готовых блоков, подключаем механику и запускаем — при горящих сроках за несколько часов.',
    leadEn:
      'A single-page promo site: assembled from ready-made blocks, wired to your mechanic and live — within hours when the deadline is burning.',
    primaryCta: {
      label: 'Запустить лендинг от 2х дней',
      labelEn: 'Launch in 2 days',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'what-is',
        title: 'Что такое промо-лендинг и когда его хватает',
        titleEn: 'What a promo landing is and when it is enough',
        kind: 'prose',
        paragraph:
          'Промо-лендинг — это одна страница, на которой человек узнаёт про акцию, регистрируется и отправляет чек или код. Всё главное на первом экране: приз, условие участия, кнопка. Такой формат берут, когда акция короткая, механика одна, а сайта ещё нет, хотя реклама уже оплачена. Если акции нужны личный кабинет, отдельные разделы и панель администратора для вашей команды — это уже промо-сайт акции, и собирается он на той же платформе.',
        paragraphEn:
          'A promo landing is a single page where a person learns about the campaign, signs up and submits a receipt or a code. Everything that matters sits on the first screen: the prize, the condition, the button. Brands choose this format when the campaign is short, the mechanic is a single one and speed matters most — the media is already paid for and there is still no site. If the campaign needs a personal account, separate sections and an admin panel for your team, that is a promo site, built on the same platform.',
      },
      {
        id: 'why',
        title: 'Почему лендинг для акции собирается быстро',
        titleEn: 'Why a campaign landing goes live fast',
        kind: 'bullets',
        items: [
          'Страница собирается в нашем конструкторе промо из готовых блоков — без программистов и без вёрстки с нуля.',
          'Шаблоны экономят до двух месяцев, которые обычно уходят на дизайн, вёрстку и сборку.',
          'Механики Promokit — коды, чеки, розыгрыши, конкурсы — подключаются к странице сразу, отдельно программировать их не нужно.',
          'Промо задерживает не разработка, а согласования и ловля ошибок на разных телефонах.',
          'Больше 900 промо-лендингов за восемь лет: типовые грабли мы уже собрали за вас.',
        ],
        itemsEn: [
          'The page is assembled in our promo builder from ready-made blocks — no developers, no markup from scratch.',
          'Templates save up to two months normally spent on design, markup and assembly.',
          'Promokit mechanics — codes, receipts, prize draws, contests — plug into the page straight away and need no separate development.',
          'What delays a promo is not development but approvals and chasing bugs across devices. With templates there is little left to argue about.',
          'Over 900 promo landings in eight years: we have already stepped on the typical rakes for you.',
        ],
      },
      {
        id: 'blocks',
        title: 'Из чего собирается страница акции',
        titleEn: 'What the campaign page is built from',
        kind: 'bullets',
        items: [
          'Первый экран: приз, условие участия и кнопка регистрации.',
          'Блок «Действия по шагам» — купи, загрузи чек, участвуй в розыгрыше.',
          'Регистрация и вход: почта, СМС, соцсети, мессенджеры. Плюс 11 готовых всплывающих окон.',
          'Блок победителей с поиском, фильтрами и разбивкой по неделям или турам.',
          'Правила, частые вопросы, партнёры акции, шапка с меню и подвал.',
          'Таймер обратного отсчёта до розыгрыша и Zero Block — пустой блок под свою вёрстку, в него переносится макет из Figma.',
        ],
        itemsEn: [
          'A first screen with the prize, the entry condition and the sign-up button.',
          'A step-by-step block — buy, upload the receipt, join the draw.',
          'Sign-up and login by email, SMS, social networks and messengers, plus a library of 11 pop-ups.',
          'A winners block with search, filters and a breakdown by weeks or rounds.',
          'Rules, FAQ, campaign partners, a header with a menu and a footer.',
          'A countdown timer to the draw and Zero Block — an empty block for your own markup, where a Figma layout can be moved in.',
        ],
      },
      {
        id: 'express',
        title: 'Срочный запуск: автолендинг',
        titleEn: 'Urgent launch: the auto-landing',
        kind: 'prose',
        paragraph:
          'Агентство не успело с дизайном, а даты рекламы уже не сдвинуть — обычная история. На этот случай есть автолендинг: вы загружаете брендированный контент и включаете акцию в несколько кликов, без разработчиков. Страницу дорабатываете на ходу: контент меняется и после старта, статистика идёт с первого дня.',
        paragraphEn:
          'The agency missed the design deadline and the media dates cannot be moved — a familiar story. For exactly that there is the auto-landing: you upload branded content and switch the campaign on in a few clicks, with no developers. After that you refine the page as you go — content can be changed after the start, and statistics are collected from day one.',
      },
      {
        id: 'speed',
        title: 'Скорость загрузки и телефоны — где промо теряет людей',
        titleEn: 'Load speed and phones — where promos lose people',
        kind: 'bullets',
        items: [
          'Большинство участников открывает промо с телефона, поэтому версия для телефона важнее, чем для компьютера.',
          'Тяжёлые картинки и лишние анимации на медленном интернете убивают страницу: человек уходит, не дождавшись контента.',
          'Внешние виджеты часто тормозят — на платформе их место занимают встроенные модули.',
          'Высокий балл в Google PageSpeed заложен в шаблоны, а не доделывается после запуска.',
        ],
        itemsEn: [
          'At least 70% of participants open a promo on a phone, so the mobile version matters more than the desktop one.',
          'Heavy images and excess animation kill the page on a slow connection: people leave before the content arrives.',
          'External widgets often lag on their own requests — on the platform their place is taken by built-in modules.',
          'A high Google PageSpeed score is built into the templates rather than patched in after launch.',
        ],
      },
      {
        id: 'mechanics',
        title: 'Какие механики подключаются к промо-лендингу',
        titleEn: 'Which mechanics plug into a promo landing',
        kind: 'bullets',
        items: [
          'Чековая: человек загружает чек, платформа сверяет его с ФНС, находит товары бренда и торговую сеть.',
          'Кодовая: коды с упаковки проверяются по базе, генератор выпускает от одного кода до нескольких миллионов.',
          'Розыгрыши автоматические и ручные — ежедневные, еженедельные, ежемесячные и главные.',
          'Конкурсы, квизы, викторины и простые игры — отдельно или в дополнение к чекам.',
          'Приём чеков через чат-бота в Телеграме, ВКонтакте или WhatsApp — второй вход в ту же акцию.',
        ],
        itemsEn: [
          'Receipt-based: a person uploads a receipt, the platform verifies it against the tax service, finds the brand products and the retail chain.',
          'Code-based: codes from the packaging are checked against the database, and the generator issues anything from one code to several million.',
          'Prize draws, automatic and manual — daily, weekly, monthly and grand ones.',
          'Contests, quizzes and simple games — on their own or on top of receipts.',
          'Receipts accepted through a chatbot in Telegram, VK or WhatsApp — a second entrance into the same campaign.',
        ],
      },
      {
        id: 'workflow',
        title: 'Как идёт работа',
        titleEn: 'How the work goes',
        kind: 'numbered',
        items: [
          'Бриф: что за акция, какие призы и условия, к какой дате нужен старт.',
          'Прототип и дизайн — по нашему шаблону или по вашему фирменному стилю.',
          'Сборка страницы в конструкторе и подключение механики.',
          'Проверка на телефонах и в браузерах, тестовый прогон механики, запуск.',
          'Сопровождение: круглосуточный мониторинг, правки по ходу акции, выделенный менеджер.',
        ],
        itemsEn: [
          'The brief: what the campaign is, which prizes and conditions, the date it has to start.',
          'Prototype and design — on our template or in your brand style.',
          'Assembling the page in the builder and connecting the mechanic.',
          'Testing on phones and browsers, a trial run of the mechanic, go-live.',
          'Support: round-the-clock monitoring, edits during the campaign, a dedicated manager.',
        ],
      },
      {
        id: 'audience',
        title: 'Кому подходит',
        titleEn: 'Who it fits',
        kind: 'prose',
        paragraph:
          'Производителям продуктов питания и товаров повседневного спроса, торговым сетям и агентствам, которые регулярно запускают потребительские акции. Лендинга хватает, когда акция идёт несколько недель, механика одна и от участника нужно одно действие. Если призов много и люди будут возвращаться за статусом чеков и баллами — сразу берите промо-сайт с личным кабинетом.',
        paragraphEn:
          'Food producers and makers of everyday consumer goods, retail chains and agencies that run consumer campaigns regularly. A landing is enough when the campaign lasts a few weeks, the mechanic is a single one and the participant has one action to take. If there are many prizes and people will come back to check receipt statuses and points, go straight for a promo site with a personal account: moving mid-campaign costs more.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'За сколько запустите промо-лендинг?',
            answer:
              'Зависит от того, что нужно. Экспресс-страницу на готовом шаблоне поднимаем за несколько часов, лендинг под ключ с уникальным дизайном — в среднем за три недели. Точный срок называем после брифа: он зависит от механики и числа согласований.',
          },
          {
            title: 'Можно ли разместить лендинг на нашем домене?',
            answer:
              'Да. Обычно сайт подключается к вашему домену записями DNS, иногда — кодом на существующем сайте бренда. Данные при этом хранятся и шифруются на наших серверах, а для крупных компаний возможен вариант с размещением на ваших.',
          },
          {
            title: 'Кто делает дизайн и тексты?',
            answer:
              'Дизайн и вёрстку берём на себя: по стандартному шаблону, по шаблону с изменениями или по новому макету. Свой дизайн тоже подойдёт — соберём по нему. Условия, правила и описания призов приходят от вас, а мы помогаем уложить их в структуру страницы.',
          },
          {
            title: 'Что с мобильной версией?',
            answer:
              'Шаблоны изначально собраны под телефоны, потому что оттуда приходит большая часть участников. До старта проверяем страницу на разных экранах и браузерах и стараемся не перегружать её анимациями: на медленном интернете они обходятся дороже, чем эффект от них.',
          },
          {
            title: 'Выдержит ли сайт наплыв в первые дни?',
            answer:
              'Страница работает на наших серверах, нагрузку закладываем заранее по прогнозу числа участников, а тяжёлую часть — проверку чеков и розыгрыши — берёт на себя движок платформы. За сайтом и механиками следим круглосуточно, а не только в рабочие часы.',
          },
          {
            title: 'Можно ли менять страницу, пока акция идёт?',
            answer:
              'Да, это обычная практика. Контент, картинки и блоки меняются на ходу: таймер после розыгрыша заменяется списком победителей, лишние блоки скрываются. Правки по контенту входят в сопровождение, изменения в саму механику акции считаются отдельно.',
          },
          {
            title: 'Сколько полей делать в форме регистрации?',
            answer:
              'По нашей статистике одно-два дополнительных обязательных поля не дают заметной разницы в конверсии, зато собранные данные потом помогают сегментации и повторным коммуникациям. От опытных призоловов формой всё равно не спастись — с ними борются другими средствами.',
          },
          {
            title: 'Чем лендинг отличается от промо-сайта акции?',
            answer:
              'Лендинг — одна страница, где человек регистрируется и отправляет чек или код. Промо-сайт — проект целиком: личный кабинет со статусами и баллами, отдельные разделы с правилами и победителями, панель администратора для вашей команды.',
          },
          {
            title: 'Что остаётся у бренда после акции?',
            answer:
              'Все собранные данные: участники, чеки и коды, выданные призы, обращения в обратную связь. Выгружаются в Excel по нажатию кнопки. Показатели акции остаются в панели, и отчёт можно собрать уже после того, как промо закончилось.',
          },
        ],
        itemsEn: [
          {
            title: 'How fast can you launch a promo landing?',
            answer:
              'It depends on what you need. An express page on a ready template goes live within hours; a turnkey landing with a unique design takes about three weeks on average. We name the exact date after the brief: it depends on the mechanic and the number of approvals.',
          },
          {
            title: 'Can the landing sit on our own domain?',
            answer:
              'Yes. The site is usually connected to your domain via DNS records, sometimes by code on the existing brand site. Data is stored and encrypted on our servers, and for large companies it can be hosted on yours.',
          },
          {
            title: 'Who does the design and the copy?',
            answer:
              'Design and markup are on us: a standard template, a template with changes, or a new layout. Your own design works too — we will build on it. Conditions, rules and prize descriptions come from you, and we help fit them into the page structure.',
          },
          {
            title: 'What about the mobile version?',
            answer:
              'The templates are built for phones from the start, because at least 70% of participants come from there. Before launch we check the page across screens and browsers and avoid overloading it with animation: on a slow connection it costs more than it gives.',
          },
          {
            title: 'Will the site survive the rush of the first days?',
            answer:
              'The page runs on our servers, the load is planned in advance from the forecast number of participants, and the heavy part — receipt validation and prize draws — is handled by the platform engine. We watch the site and the mechanics around the clock, not only in office hours.',
          },
          {
            title: 'Can the page be changed while the campaign is running?',
            answer:
              'Yes, that is normal practice. Content, images and blocks change on the fly: after the draw the timer is replaced by the winners list, unneeded blocks are hidden. Content edits are part of support; changes to the mechanic itself are quoted separately.',
          },
          {
            title: 'How many fields should the sign-up form have?',
            answer:
              'Our statistics show that one or two extra required fields make no visible difference to conversion, while the data collected later helps segmentation and repeat communication. Form length will not stop experienced prize hunters anyway — they are dealt with by other means.',
          },
          {
            title: 'How is a landing different from a promo site?',
            answer:
              'A landing is one page where a person signs up and submits a receipt or a code. A promo site is the whole project: a personal account with statuses and points, separate sections for rules and winners, and an admin panel for your team.',
          },
          {
            title: 'What does the brand keep after the campaign?',
            answer:
              'All the collected data: participants, receipts and codes, prizes issued, support requests. It exports to Excel at the click of a button. Campaign figures stay in the panel, and a report can be assembled after the promo is over.',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы запустить эффективный промо-лендинг в сжатые сроки?',
      headingEn: 'Ready to launch a high-impact promo landing on a tight timeline?',
      lead: 'Напишите нам — ответим в течение 1 часа.',
      leadEn: 'Drop us a line — we reply within an hour.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 2. Промо-акция с кодовой механикой                                       */
  /* ------------------------------------------------------------------------ */
  {
    id: 'promo-code',
    seoTitle: 'Кодовая промо-акция под ключ',
    seoTitleEn: 'Code-based promo campaigns',
    heading: 'Промо-акции с кодами на упаковке: генерация, регистрация, розыгрыш',
    headingEn: 'Code-based promotions on the Promokit platform',
    lead: 'Акции «код под крышкой» и промокод на упаковке: генерируем коды, проверяем мгновенно, начисляем баллы и разыгрываем призы на платформе Promokit.',
    leadEn:
      'Code-under-the-cap and on-pack promo code campaigns: we generate the codes, validate them instantly, award points and run the draws.',
    primaryCta: {
      label: 'Запустить кодовую акцию',
      labelEn: 'Launch a code-based promo',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'advantages',
        title: 'Преимущества кодовой акции',
        titleEn: 'Advantages of a code-based promo',
        kind: 'bullets',
        items: [
          'Собственная платформа Promokit, проверенная восемью годами активной эксплуатации',
          'Встроенный генератор кодов: от одного до нескольких миллионов уникальных значений',
          'Мгновенная проверка кода — розыгрыш можно проводить в тот же день',
          'Полный цикл: от регистрации участника до публикации победителей',
          'Кешбэк, начисление баллов и выбор приза из каталога',
          'Коробочные и кастомные игры, квизы и викторины поверх основной механики',
          'Маркетинговые рассылки по email и SMS',
          'ИИ помогает разбирать обращения участников',
          'Гибкая настройка вывода победителей на сайте акции',
          'Дешевле чековой механики: не нужен модуль валидации чеков в ФНС и ручная модерация',
        ],
        itemsEn: [
          'Our own Promokit platform, battle-tested over eight years in production',
          'A built-in code generator: from one to several million unique values',
          'Instant code validation — a draw can be held the same day',
          'End-to-end flow: from participant sign-up to publishing the winners',
          'Cashback, point accrual and prize selection from a catalogue',
          'Out-of-the-box and custom games, quizzes and trivia on top of the core mechanic',
          'Email and SMS marketing campaigns',
          'AI helps handle participant tickets',
          'Flexible winner announcements on the campaign site',
          'Cheaper than receipt mechanics: no tax-service validation module and no manual moderation',
        ],
      },
      {
        id: 'how-it-works',
        title: 'Как работает акция с промокодами на упаковке',
        titleEn: 'How an on-pack promo code campaign works',
        kind: 'numbered',
        items: [
          'Покупатель находит код под крышкой, на этикетке или на вкладыше внутри упаковки',
          'Регистрируется на сайте акции — по телефону, почте, через соцсеть или мессенджер',
          'Вводит код в личном кабинете или отправляет его в чат-бота',
          'Система мгновенно сверяет код с базой и проверяет, что его ещё не вводили',
          'За верный код начисляются баллы, шансы в розыгрыше или мгновенный приз',
          'Баллы копятся и обмениваются на приз из каталога',
          'Розыгрыш проходит автоматически по заданной формуле или вручную',
          'Победители подтверждаются и выходят в отдельном разделе сайта',
        ],
        itemsEn: [
          'The shopper finds a code under the cap, on the label or on an insert inside the pack',
          'Signs up on the campaign site — by phone, email, social network or messenger',
          'Enters the code in the dashboard or sends it to a chatbot',
          'The system instantly matches the code against the database and checks it has not been used',
          'A valid code earns points, draw entries or an instant prize',
          'Points accumulate and are exchanged for a prize from the catalogue',
          'The draw runs automatically by a set formula, or manually',
          'Winners are confirmed and published in a dedicated section of the site',
        ],
      },
      {
        id: 'code-generation',
        title: 'Как мы делаем генерацию кодов для промо',
        titleEn: 'How we generate the codes for a promo',
        kind: 'prose',
        paragraph:
          'Коды создаёт встроенный модуль генерации: от одного до нескольких миллионов уникальных значений за раз. Алгоритм выбираете вы — можно взять наш обкатанный или собрать новый под требования упаковки и типографии. Готовую базу мы передаём вам для нанесения под крышку, на этикетку или на вкладыш внутри пачки, а её копия остаётся в платформе, чтобы сверять вводимые коды мгновенно. Если генерация уже налажена на вашей стороне или этим занимается производство, мы спокойно принимаем готовую базу и делимся рекомендациями, как её собрать.',
        paragraphEn:
          'Codes are produced by the built-in generation module: from one to several million unique values at a time. You choose the algorithm — either our proven one or a new one built around your packaging and printing requirements. We hand the finished database over for printing under the cap, on the label or on an insert, and keep a copy in the platform so that submitted codes are matched instantly. If code generation is already handled on your side or by your production line, we are happy to take a ready database and share recommendations on how to build it.',
      },
      {
        id: 'code-protection',
        title: 'Защита кодов и акции от накруток',
        titleEn: 'Protecting the codes and the campaign from cheating',
        kind: 'bullets',
        items: [
          'Каждый код одноразовый: система проверяет уникальность и не даёт ввести его дважды',
          'Алгоритм генерации подбирается под акцию — берём проверенный или делаем новый',
          'База кодов хранится на стороне платформы, а не раздаётся наружу',
          'Условия акции ограничивают число кодов на участника и период их приёма',
          'Регистрация по телефону, почте или соцсети затрудняет создание мультиаккаунтов',
          'Аналитика в реальном времени показывает всплески активности по ходу акции',
          'Победители подтверждаются перед выдачей приза',
        ],
        itemsEn: [
          'Every code is single-use: the system checks uniqueness and will not accept it twice',
          'The generation algorithm is chosen per campaign — a proven one or a new one',
          'The code database stays on the platform side rather than being handed around',
          'Campaign rules cap codes per participant and the window for submitting them',
          'Sign-up by phone, email or social account makes multi-accounting harder',
          'Real-time analytics shows activity spikes as the campaign runs',
          'Winners are confirmed before a prize is issued',
        ],
      },
      {
        id: 'capabilities',
        title: 'Возможности Promokit для кодовых акций',
        titleEn: 'Promokit capabilities for code-based promos',
        kind: 'bullets',
        items: [
          'Авторизация через соцсети, телефон или email',
          'Личный кабинет с историей кодов, баллов и статусом заказа призов',
          'Приём кодов через чат-бота в Telegram, ВКонтакте или WhatsApp',
          'Автоматические розыгрыши — ежечасные, ежедневные, еженедельные, ежемесячные и главные',
          'Моментальный вывод денежных призов на карту или телефон',
          'Электронные призы: сертификат, PDF и ссылка в личном кабинете',
          'Реферальные ссылки другу и репосты в соцсети за дополнительные шансы',
          'Аналитика в реальном времени и выгрузка отчётов в Excel',
          'Интеграция по API с CRM, ERP, картами лояльности и корпоративной шиной',
          'Генератор фирменных QR-кодов для упаковки и рекламных носителей',
        ],
        itemsEn: [
          'Sign-in via social, phone or email',
          'A dashboard with code history, points and prize order status',
          'Code intake through a Telegram, VK or WhatsApp bot',
          'Automated draws — hourly, daily, weekly, monthly and for the grand prizes',
          'Instant cash payouts to a card or phone number',
          'Digital prizes: a certificate, a PDF and a link in the dashboard',
          'Referral links and social shares for extra entries',
          'Real-time analytics and Excel report exports',
          'API integration with CRM, ERP, loyalty cards and a corporate data bus',
          'A branded QR code generator for packaging and advertising materials',
        ],
      },
      {
        id: 'launch',
        title: 'Как проходит запуск кодового промо',
        titleEn: 'How a code promo launch runs',
        kind: 'numbered',
        items: [
          'Собираем бриф и техническое задание — со сметой, планом сроков и картой пути участника',
          'Генерируем базу кодов и передаём её к нанесению на упаковку',
          'Рисуем прототип и дизайн сайта акции',
          'Собираем сайт на конструкторе Bafsy из готовых блоков и проверяем по чек-листу из 63 пунктов',
          'Подключаем движок: валидацию кодов, баллы, розыгрыши, обратную связь, аналитику, интеграции',
          'Ведём акцию до конца: круглосуточный мониторинг, выделенный менеджер, правки на ходу',
        ],
        itemsEn: [
          'We collect the brief and write the spec, with a budget, a timeline and a customer journey map',
          'We generate the code database and hand it over for printing on the packaging',
          'We design the prototype and the visual look of the campaign site',
          'We build the site in the Bafsy builder and run it through a 63-point quality checklist',
          'We connect the engine: code validation, points, draws, support, analytics, integrations',
          'We run the campaign to the finish: 24/7 monitoring, a dedicated manager, changes on the fly',
        ],
      },
      {
        id: 'code-or-receipt',
        title: 'Кодовая или чековая механика — что выбрать',
        titleEn: 'Code or receipt mechanics — which to choose',
        kind: 'prose',
        paragraph:
          'Кодовая механика выигрывает, когда на упаковку можно нанести уникальный код: проверка занимает доли секунды, розыгрыш можно проводить в тот же день, а сама акция обходится дешевле — не нужен ни модуль валидации чеков в ФНС, ни ручная модерация загрузок. Взамен она требует подготовки производства: коды надо сгенерировать заранее и напечатать вместе с тиражом упаковки. Чековая механика такой подготовки не требует и подходит любой рознице, но проверка чека занимает время и стоит дороже. Если тираж упаковки уже в работе или код нанести некуда, честнее сразу смотреть в сторону чеков.',
        paragraphEn:
          'Code mechanics win when a unique code can be printed on the pack: validation takes a fraction of a second, a draw can run the same day, and the campaign costs less — no tax-service validation module and no manual moderation of uploads. In return it requires production lead time: codes must be generated in advance and printed with the packaging run. Receipt mechanics need none of that preparation and work with any retail channel, but verification takes time and costs more. If the packaging run is already underway or there is nowhere to put a code, receipts are the honest answer.',
      },
      {
        id: 'audience',
        title: 'Каким брендам подходит кодовое промо',
        titleEn: 'Which brands a code promo suits',
        kind: 'prose',
        paragraph:
          'Кодовая механика создана для брендов, у которых есть возможность нанести уникальный код на упаковку: под крышку напитка, на внутреннюю сторону этикетки, на вкладыш в пачке снеков или бытовой химии. Хорошо работает там, где покупка частая и повторная, — тогда баллы копятся, а участник возвращается за следующей пачкой. Подходит и для акций на отдельную позицию линейки: коды печатаются только на нужном тираже, и в акции участвует ровно тот товар, который вы продвигаете.',
        paragraphEn:
          'Code mechanics are built for brands able to print a unique code on the pack: under a drinks cap, on the inside of a label, on an insert in a pack of snacks or household chemicals. They work best where purchases are frequent and repeated — points accumulate and the participant comes back for the next pack. They also suit campaigns for a single item in the range: codes are printed only on the run you need, so exactly the product you are promoting takes part.',
      },
      {
        id: 'budget',
        title: 'Сколько стоит кодовая акция',
        titleEn: 'What a code-based promo costs',
        kind: 'prose',
        paragraph:
          'Смета собирается из модулей, и вы платите только за нужные. Базовый набор для кодового промо — дизайн и сборка сайта, генерация кодов и модуль валидации кодов; дальше по выбору идут накопительная система, управление розыгрышами, выдача электронных призов, рассылки, чат-бот, игры и интеграции с вашими системами. По сравнению с чековой акцией из сметы уходит самая дорогая часть — валидация чеков в ФНС и ручная проверка, — поэтому кодовая механика и получается бюджетнее. Калькулятор на сайте проведёт по семи шагам и пришлёт смету с зафиксированной ценой в Excel.',
        paragraphEn:
          'The estimate is assembled from modules and you pay only for the ones you need. The base set for a code promo is site design and build, code generation and the code validation module; a points system, draw management, digital prize delivery, mailings, a chatbot, games and integrations are added by choice. Compared with a receipt campaign, the most expensive part drops out — tax-service validation and manual review — which is why code mechanics come out cheaper. The calculator on the site walks through seven steps and returns a fixed-price estimate in Excel.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы о кодовых акциях',
        titleEn: 'Code promo FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Для кого подходит кодовая механика?',
            answer:
              'Для брендов, у которых есть возможность нанести уникальный код на упаковку: под крышку, на этикетку или на вкладыш внутри пачки. Особенно хорошо она работает на товарах частой повторной покупки, где участнику есть смысл копить баллы и возвращаться за следующей упаковкой.',
          },
          {
            title: 'Кто создаёт базу кодов?',
            answer:
              'Базу уникальных кодов можем сделать мы — во встроенном модуле генерации. Если у вас есть возможность собрать её самостоятельно или этим занимается производство, мы готовы передать эту часть на вашу сторону и поделиться рекомендациями, как собрать базу правильно.',
          },
          {
            title: 'Сколько кодов можно сгенерировать?',
            answer:
              'Модуль генерации выпускает от одного до нескольких миллионов уникальных кодов за раз, так что тираж упаковки ограничением не станет. Алгоритм подбирается под акцию: можно взять наш обкатанный или сделать новый под требования упаковки и типографии.',
          },
          {
            title: 'Как быстро проверяется код?',
            answer:
              'Практически мгновенно: введённый код сверяется с базой в момент отправки, и участник сразу видит результат. Именно поэтому кодовая механика позволяет проводить розыгрыши в тот же день, вплоть до ежечасных, и выдавать мгновенные призы.',
          },
          {
            title: 'Что мешает ввести чужой или выдуманный код?',
            answer:
              'Код должен совпасть с записью в базе, а каждое значение одноразовое: система проверяет уникальность и второй раз тот же код не примет. Условия акции дополнительно ограничивают число кодов на участника, а победители подтверждаются перед выдачей приза.',
          },
          {
            title: 'Сколько стоит кодовая акция?',
            answer:
              'Стоимость собирается из модулей: дизайн и сборка сайта, генерация кодов, валидация кодов, розыгрыши, рассылки, интеграции. Валидация чеков в ФНС и ручная модерация здесь не нужны, поэтому смета выходит меньше, чем у чековой акции. Точную цифру даст калькулятор.',
          },
          {
            title: 'Сколько времени занимает запуск?',
            answer:
              'Техническая часть собирается быстро: сайт по готовому шаблону поднимается в экспресс-режиме за часы, под ключ — в среднем за три недели. Главный срок обычно задаёт не разработка, а производство: коды нужно сгенерировать и напечатать вместе с тиражом упаковки.',
          },
          {
            title: 'Можно ли принимать коды без сайта, в чат-боте?',
            answer:
              'Да. Бот в Telegram, ВКонтакте или WhatsApp умеет регистрировать участника, принимать коды, показывать личный кабинет с баллами и подарками, отвечать на частые вопросы и собирать обращения. Работать он может как дополнение к сайту акции или полностью автономно.',
          },
          {
            title: 'Кто отвечает за налоги с призов?',
            answer:
              'Налоговая сторона остаётся за организатором акции и описывается в её правилах — это не техническая часть платформы. Мы даём то, что нужно для отчётности: подтверждение победителей и выгрузку по участникам, кодам, розыгрышам и выданным призам в Excel.',
          },
        ],
        itemsEn: [
          {
            title: 'Who are code mechanics right for?',
            answer:
              'For brands able to print a unique code on the pack: under the cap, on the label or on an insert inside. They work especially well on frequently repurchased goods, where it makes sense for a participant to accumulate points and come back for the next pack.',
          },
          {
            title: 'Who creates the code database?',
            answer:
              'We can create the unique code database in our built-in generation module. If you are able to build it yourself, or your production line handles it, we are happy to hand that part over to your side and share recommendations on building it correctly.',
          },
          {
            title: 'How many codes can be generated?',
            answer:
              'The generation module issues from one to several million unique codes at a time, so your packaging run will not be the limit. The algorithm is chosen per campaign: our proven one, or a new one built around packaging and printing requirements.',
          },
          {
            title: 'How fast is a code validated?',
            answer:
              'Almost instantly: the submitted code is matched against the database on send, and the participant sees the result straight away. That is why code mechanics allow same-day draws, right down to hourly ones, and instant prizes.',
          },
          {
            title: 'What stops someone entering a stolen or made-up code?',
            answer:
              'The code has to match a record in the database, and every value is single-use: the system checks uniqueness and will not accept the same code twice. Campaign rules further cap codes per participant, and winners are confirmed before a prize is issued.',
          },
          {
            title: 'What does a code-based promo cost?',
            answer:
              'The cost is assembled from modules: site design and build, code generation, code validation, draws, mailings, integrations. Tax-service receipt validation and manual moderation are not needed here, so the estimate comes out lower than for a receipt campaign.',
          },
          {
            title: 'How long does the launch take?',
            answer:
              'The technical side comes together fast: a site on a ready template goes up within hours in express mode, or about three weeks turnkey. The real timeline is usually set by production, not development: codes must be generated and printed with the packaging run.',
          },
          {
            title: 'Can codes be collected without a website, in a chatbot?',
            answer:
              'Yes. A bot in Telegram, VK or WhatsApp can register a participant, accept codes, show a dashboard with points and prizes, answer common questions and collect support tickets. It can work alongside the campaign site or entirely on its own.',
          },
          {
            title: 'Who is responsible for taxes on prizes?',
            answer:
              'The tax side stays with the campaign organiser and is set out in the campaign rules — it is not part of the platform. We provide what reporting needs: winner confirmation and Excel exports of participants, codes, draws and prizes issued.',
          },
        ],
      },
    ],
    closing: {
      heading: 'Результаты с Bafsy',
      headingEn: 'Results with Bafsy',
      lead: 'Более 900 реализованных промо-сайтов, 249+ постоянных клиентов, успешный опыт проведения акций по всей России и за рубежом.',
      leadEn:
        'Over 900 promo sites delivered, 249+ recurring clients, and a track record of campaigns across Russia and beyond.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 3. Промо-акция с чековой механикой                                       */
  /* ------------------------------------------------------------------------ */
  {
    id: 'promo-receipt',
    seoTitle: 'Чековая акция под ключ с проверкой ФНС',
    seoTitleEn: 'Receipt promo with tax-service checks',
    heading: 'Промоакции с загрузкой чеков — с интеграцией ФНС и проверкой подлинности',
    headingEn:
      'Receipt-based promotions with Federal Tax Service integration and authenticity checks',
    lead: 'Акции «купи и загрузи чек»: распознаём чек, сверяем с ФНС, начисляем баллы и проводим розыгрыши. Чековая акция под ключ на платформе Promokit.',
    leadEn:
      'Buy-and-upload-your-receipt campaigns: we read the receipt, verify it with the tax service, award points and run the draws.',
    primaryCta: {
      label: 'Запустить чековую акцию',
      labelEn: 'Launch a receipt-based promo',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'key-advantages',
        title: 'Ключевые преимущества чековой акции на Promokit',
        titleEn: 'Key advantages of a receipt promo on Promokit',
        kind: 'bullets',
        items: [
          'Готовая интеграция с ФНС: проверяем, что чек существует и покупка действительно была',
          'Готовые интеграции с операторами фискальных данных — реквизиты чека берём из первоисточника',
          'Автоматическое распознавание товаров бренда в номенклатуре чека и определение торговой сети',
          'Загрузка чека фотографией или по QR-коду — прямо с телефона, за несколько секунд',
          'Ручная модерация чеков, которые не прошли автоматическую проверку',
          'Личный кабинет со статусом каждого чека, баллами и историей призов',
          'Автоматический запрос призов, коробочные и кастомные игры, кешбэк',
          'Мотивационные механики для сотрудников торговых точек',
          'ИИ помогает разбирать обращения участников, а сложное берёт на себя оператор',
        ],
        itemsEn: [
          'Ready-made tax-service integration: we confirm the receipt exists and the purchase really happened',
          'Ready integrations with fiscal data operators — receipt details come straight from the source',
          'Automatic recognition of your products inside the receipt and identification of the retail chain',
          'Receipt upload by photo or QR code — straight from a phone, in seconds',
          'Manual moderation for receipts that fail the automatic check',
          'A participant dashboard with the status of every receipt, points and prize history',
          'Automatic prize claims, out-of-the-box and custom games, cashback',
          'Incentive mechanics for retail staff',
          'AI handles routine participant tickets, an operator picks up the rest',
        ],
      },
      {
        id: 'how-it-works',
        title: 'Как проходит регистрация чеков со стороны покупателя',
        titleEn: 'How receipt registration works for the shopper',
        kind: 'numbered',
        items: [
          'Покупатель берёт акционный товар в любой рознице и сохраняет кассовый чек',
          'Регистрируется на сайте акции — по телефону, почте, через соцсеть или мессенджер',
          'Загружает чек: фотографирует его целиком или сканирует QR-код на чеке',
          'Платформа распознаёт чек, находит в нём товары бренда и определяет торговую сеть',
          'Реквизиты уходят на сверку с ФНС — так подтверждается сама покупка',
          'В личном кабинете виден статус чека: принят, на проверке или отклонён',
          'За принятый чек начисляются баллы или шансы в розыгрыше — по условиям вашей акции',
          'Победитель запрашивает приз в кабинете, а его имя выходит в разделе победителей',
        ],
        itemsEn: [
          'The shopper buys a participating product in any store and keeps the receipt',
          'Signs up on the campaign site — by phone, email, social network or messenger',
          'Uploads the receipt: a full photo of it, or a scan of its QR code',
          'The platform reads the receipt, finds your products in it and identifies the retail chain',
          'The details go to the tax service for verification, confirming the purchase itself',
          'The dashboard shows the status of the receipt: accepted, under review or rejected',
          'An accepted receipt earns points or draw entries, following your campaign rules',
          'The winner claims the prize in the dashboard and appears in the winners section',
        ],
      },
      {
        id: 'validation',
        title: 'Как устроена валидация и модерация чеков',
        titleEn: 'How receipt validation and moderation work',
        kind: 'prose',
        paragraph:
          'Проверка идёт в два круга. Первый — автоматический: модуль обработки чеков распознаёт изображение, собирает список позиций, сверяет их со списком акционных товаров, определяет сеть, а реквизиты чека отправляет на сверку с ФНС — там подтверждается, что такая покупка действительно была пробита на кассе. Данные платформа берёт напрямую: у нас есть доступ к API ФНС и готовые интеграции с операторами фискальных данных. Второй круг — ручной: чеки, которые автоматика отклонила, просматривает модератор. Чаще всего причина не в подделке, а в смазанном фото, блике или выцветшей термобумаге, поэтому такой чек мы сверяем с правилами акции руками и, если всё честно, засчитываем.',
        paragraphEn:
          'Verification runs in two rounds. First, automatically: the receipt module reads the image, extracts the line items, matches them against your list of participating products, identifies the retail chain and sends the receipt details to the tax service, which confirms the purchase was actually rung up at a till. The data comes first-hand — we have access to the tax service API and ready integrations with fiscal data operators. The second round is manual: every receipt the automation rejects is reviewed by a moderator. Usually the cause is not forgery but a blurry photo, glare or faded thermal paper, so we check such a receipt against the campaign rules by hand and count it if everything is genuine.',
      },
      {
        id: 'antifraud',
        title: 'Защита от призоловов и накруток',
        titleEn: 'Protection against prize hunters and cheating',
        kind: 'bullets',
        items: [
          'Один чек засчитывается один раз: повторная загрузка того же чека отсекается',
          'Сверка с ФНС отсекает нарисованные и смонтированные чеки — в базе их просто нет',
          'Условия из правил акции: минимальная сумма чека, категории и список товаров, число чеков на участника',
          'Проверка даты покупки — чек должен попадать в период акции',
          'Фильтрация авансовых чеков с маркетплейсов, по которым товар ещё не выдан',
          'Спорные загрузки уходят к модератору, а не в автоматический отказ',
          'Победители подтверждаются вручную перед выдачей приза',
        ],
        itemsEn: [
          'A receipt counts once: re-uploading the same one is filtered out',
          'Tax-service verification rejects drawn and edited receipts — they simply are not in the database',
          'Rules-based limits: minimum receipt amount, product categories and lists, receipts per participant',
          'Purchase-date check — the receipt must fall inside the campaign period',
          'Filtering of marketplace prepayment receipts, where the goods have not been handed over yet',
          'Disputed uploads go to a moderator instead of an automatic refusal',
          'Winners are confirmed manually before a prize is issued',
        ],
      },
      {
        id: 'features',
        title: 'Что ещё подключается к чековой акции',
        titleEn: 'What else plugs into a receipt promo',
        kind: 'bullets',
        items: [
          'Приём чеков через чат-бота в Telegram, ВКонтакте или WhatsApp — вместе с сайтом или вместо него',
          'Накопительная система: баллы копятся, приз выбирается из каталога',
          'Автоматические розыгрыши — ежечасные, ежедневные, еженедельные, ежемесячные и главные',
          'Моментальный вывод денежных призов на карту или телефон через платёжные системы',
          'Электронные призы: сертификат, PDF и ссылка прямо в личном кабинете',
          'Публикация победителей на сайте с фильтром по неделям и типам розыгрышей',
          'Email- и SMS-рассылки, реферальные ссылки, репосты в соцсети за дополнительные шансы',
          'Выгрузка отчётов по участникам, чекам и розыгрышам в Excel',
          'Интеграция по API с вашей CRM, ERP, картами лояльности и корпоративной шиной',
        ],
        itemsEn: [
          'Receipt intake through a Telegram, VK or WhatsApp bot — alongside the site or instead of it',
          'A points system: participants accumulate points and pick a prize from a catalogue',
          'Automated draws — hourly, daily, weekly, monthly and for the grand prizes',
          'Instant cash payouts to a card or phone number through payment providers',
          'Digital prizes: a certificate, a PDF and a link right in the dashboard',
          'Winners published on the site, filterable by week and draw type',
          'Email and SMS campaigns, referral links, social shares for extra entries',
          'Excel exports of participants, receipts and draws',
          'API integration with your CRM, ERP, loyalty cards and corporate data bus',
        ],
      },
      {
        id: 'launch',
        title: 'Как мы запускаем чековую акцию под ключ',
        titleEn: 'How we launch a turnkey receipt promo',
        kind: 'numbered',
        items: [
          'Собираем бриф и техническое задание — вместе со сметой, планом сроков и картой пути участника',
          'Рисуем прототип и дизайн сайта акции',
          'Собираем сайт на конструкторе Bafsy из готовых блоков — здесь и экономится основное время',
          'Проверяем результат по чек-листу контроля качества из 63 пунктов',
          'Подключаем движок: валидацию чеков в ФНС, розыгрыши, обратную связь, аналитику, интеграции',
          'Ведём акцию до конца: круглосуточный мониторинг, выделенный менеджер, правки на ходу',
        ],
        itemsEn: [
          'We collect the brief and write the spec, with a budget, a timeline and a customer journey map',
          'We design the prototype and the visual look of the campaign site',
          'We assemble the site in the Bafsy builder from ready-made blocks — that is where the time is saved',
          'We run the result through a 63-point quality checklist',
          'We connect the engine: tax-service receipt validation, draws, support, analytics, integrations',
          'We run the campaign to the finish: 24/7 monitoring, a dedicated manager, changes on the fly',
        ],
      },
      {
        id: 'audience',
        title: 'Каким брендам подходит чековая механика',
        titleEn: 'Which brands receipt mechanics suit',
        kind: 'prose',
        paragraph:
          'Чековая механика работает там, где товар продаётся через обычную розницу, а нанести код на упаковку невозможно или слишком дорого: продукты питания, напитки, бытовая химия, корма, косметика, любые товары повседневного спроса. Она же удобна, когда в акции участвует линейка целиком, а не отдельная позиция: список акционных товаров живёт в настройках платформы и меняется по ходу кампании. В акции бренда Little One, например, участвовали все продукты линейки — больше семидесяти, — а порогом входа была покупка от 350 рублей.',
        paragraphEn:
          'Receipt mechanics work wherever the product is sold through ordinary retail and printing a code on the pack is impossible or too expensive: food, drinks, household chemicals, pet food, cosmetics, any fast-moving consumer goods. They also suit campaigns that involve a whole product line rather than a single item: the list of participating products lives in the platform settings and can be changed mid-campaign. In the Little One campaign, for instance, the entire range took part — more than seventy products — with a minimum purchase of 350 roubles.',
      },
      {
        id: 'budget',
        title: 'Сколько стоит чековая акция',
        titleEn: 'What a receipt promo costs',
        kind: 'prose',
        paragraph:
          'Точную сумму называет смета, а не прайс-лист: стоимость складывается из модулей, и вы платите только за те, что нужны вашей кампании. Базовый набор — дизайн и сборка сайта, модуль загрузки чеков для участников и модуль валидации чеков в ФНС; дальше по выбору идут розыгрыши, накопительная система, рассылки, чат-бот, игры и интеграции. Отдельно считается объём: сколько ждёте участников и сколько чеков они загрузят. Посчитать можно самому — калькулятор на сайте проведёт по семи шагам и пришлёт готовую смету с зафиксированной ценой в Excel, коммерческое предложение и план сроков.',
        paragraphEn:
          'The exact figure comes from an estimate, not a price list: the cost is assembled from modules and you pay only for the ones your campaign needs. The base set is design and build of the site, the participant receipt-upload module and the tax-service validation module; draws, a points system, mailings, a chatbot, games and integrations are added by choice. Volume is priced separately: how many participants you expect and how many receipts they will upload. You can work it out yourself — the calculator on the site walks through seven steps and sends back a fixed-price estimate in Excel, a commercial proposal and a timeline.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы о чековых акциях',
        titleEn: 'Receipt promo FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Сколько времени проверяется чек?',
            answer:
              'Автоматическая часть проходит сразу: платформа распознаёт чек и показывает его в личном кабинете. Сверка с ФНС идёт своим чередом: её скорость зависит от налоговой службы, а не от платформы. Всё это время участник видит статус «на проверке», и чек не теряется.',
          },
          {
            title: 'Что делать, если чек не распознался или его отклонили?',
            answer:
              'Все чеки со статусом «отклонён» мы просматриваем вручную. Обычно причина простая: смазанное фото, блик, обрезанный край, выцветшая термобумага. Если чек настоящий и покупка подходит под правила акции, модератор засчитывает его руками — участнику ничего доказывать не нужно.',
          },
          {
            title: 'Принимаете ли чеки с маркетплейсов и из интернет-магазинов?',
            answer:
              'Да, платформа распознаёт чеки маркетплейсов. Отдельно отфильтровываются авансовые чеки: по ним товар ещё не выдан, поэтому в акции они не участвуют. Какие каналы продаж засчитывать, а какие нет, вы решаете в условиях акции.',
          },
          {
            title: 'Как вы защищаете акцию от призоловов?',
            answer:
              'Сверка с ФНС отсекает нарисованные чеки, повторная загрузка того же чека не проходит, а условия акции ограничивают число чеков на участника, минимальную сумму и список товаров. Спорные случаи уходят к модератору, победители подтверждаются вручную перед выдачей приза.',
          },
          {
            title: 'Сколько стоит запустить чековую акцию?',
            answer:
              'Смета собирается из модулей: дизайн и сборка сайта, загрузка чеков, валидация в ФНС, розыгрыши, рассылки, интеграции — платите только за нужное. Объём считается отдельно, по числу участников и чеков. Калькулятор на сайте пришлёт смету с зафиксированной ценой в Excel.',
          },
          {
            title: 'Сколько времени занимает запуск?',
            answer:
              'Сроки зависят от технического задания. Сайт акции по готовому шаблону собирается быстро: экспресс-вариант можно поднять за считанные часы, лендинг под ключ — в среднем за три недели. Модули движка подключаются параллельно, поэтому запуск не растягивается.',
          },
          {
            title: 'Нужен ли для акции отдельный сайт?',
            answer:
              'Не обязательно. Механику можно целиком увести в чат-бота: Telegram, ВКонтакте или WhatsApp примут регистрацию, чек и обращение в поддержку. Сайт акции при этом можно интегрировать с существующим сайтом бренда или подключить к вашему домену.',
          },
          {
            title: 'Кто отвечает за налоги с призов?',
            answer:
              'Налоговая сторона остаётся за организатором акции и описывается в её правилах — это не техническая часть платформы. Со своей стороны мы даём то, что нужно для отчётности: подтверждение победителей и выгрузку по участникам, чекам, розыгрышам и выданным призам в Excel.',
          },
          {
            title: 'Где хранятся данные участников?',
            answer:
              'Возможны два варианта. Для малого и среднего бизнеса обработка и хранение идут на серверах Bafsy, данные шифруются, а сайт подключается к вашему домену по DNS. Для корпорации техническая часть — хранение и обработка — разворачивается на ваших серверах.',
          },
        ],
        itemsEn: [
          {
            title: 'How long does a receipt take to verify?',
            answer:
              'The automatic part is immediate: the platform reads the receipt and shows it in the dashboard. Tax-service verification takes up to 72 hours — their timeline, not ours. Throughout, the participant sees an "under review" status and nothing gets lost.',
          },
          {
            title: 'What happens if a receipt is unreadable or rejected?',
            answer:
              'Every receipt marked "rejected" is reviewed by hand. The cause is usually simple: a blurry photo, glare, a cropped edge, faded thermal paper. If the receipt is genuine and the purchase fits the rules, a moderator accepts it manually — the participant has nothing to prove.',
          },
          {
            title: 'Do you accept receipts from marketplaces and online stores?',
            answer:
              'Yes, the platform recognises marketplace receipts. Prepayment receipts are filtered out separately: the goods have not been handed over yet, so they do not take part. Which sales channels count is your decision, set in the campaign rules.',
          },
          {
            title: 'How do you protect a campaign from prize hunters?',
            answer:
              'Tax-service verification rejects fabricated receipts, re-uploading the same receipt fails, and the rules cap receipts per participant, the minimum amount and the product list. Disputed cases go to a moderator, and winners are confirmed manually before a prize is issued.',
          },
          {
            title: 'What does it cost to launch a receipt promo?',
            answer:
              'The estimate is assembled from modules: site design and build, receipt upload, tax-service validation, draws, mailings, integrations — you pay only for what you use. Volume is priced separately by participants and receipts. The calculator returns a fixed-price estimate in Excel.',
          },
          {
            title: 'How long does the launch take?',
            answer:
              'It depends on the spec. A campaign site built on a ready template goes up fast: an express version within hours, a full turnkey landing in about three weeks on average. Engine modules are connected in parallel, so the launch does not drag on.',
          },
          {
            title: 'Does the campaign need its own website?',
            answer:
              'Not necessarily. The whole mechanic can live in a chatbot: Telegram, VK or WhatsApp will handle sign-up, receipts and support tickets. The campaign site can also be integrated with your existing brand site or attached to your own domain.',
          },
          {
            title: 'Who is responsible for taxes on prizes?',
            answer:
              'The tax side stays with the campaign organiser and is set out in the campaign rules — it is not part of the platform. What we provide is what reporting needs: winner confirmation and Excel exports of participants, receipts, draws and prizes issued.',
          },
          {
            title: 'Where is participant data stored?',
            answer:
              'Two options. For small and mid-sized business, processing and storage sit on Bafsy servers, data is encrypted and the site is attached to your domain via DNS. For a corporation, storage and processing are deployed on your own servers.',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы запустить чековую акцию?',
      headingEn: 'Ready to launch a receipt-based promo?',
      lead: 'Расскажем о готовых шаблонах и подберём механику под ваши категории товаров.',
      leadEn:
        'We will walk you through ready-made templates and pick the mechanic that fits your product categories.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 4. Промо-акция с конкурсной механикой                                    */
  /* ------------------------------------------------------------------------ */
  {
    id: 'promo-contest',
    seoTitle: 'Фото- и видеоконкурсы для брендов',
    seoTitleEn: 'Photo and video contests for brands',
    heading: 'Фото- и видео-конкурсы с голосованием и модерацией',
    headingEn: 'Photo and video contests with voting and moderation',
    lead: 'Проводим конкурсы и розыгрыши среди покупателей: приём работ, голосование, модерация, честный выбор победителей и публикация итогов.',
    leadEn:
      'Contests and prize draws for shoppers: entry intake, voting, moderation, fair winner selection and published results.',
    primaryCta: {
      label: 'Запустить конкурс',
      labelEn: 'Launch a contest',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'formats',
        title: 'Какие конкурсы и розыгрыши мы проводим',
        titleEn: 'Contest and prize draw formats we run',
        kind: 'bullets',
        items: [
          'Фотоконкурс для покупателей: участник загружает снимок с продуктом, работа проходит модерацию и попадает в общую галерею.',
          'Видеоконкурс и творческие задания — рисунок, рецепт, история, слоган.',
          'Конкурс с голосованием: открытым, когда голосуют все, или закрытым, когда работы оценивает жюри бренда.',
          'Конкурс в социальных сетях с подтягиванием публикаций по хэштегу на сайт акции.',
          'Викторины, квизы и игры — отдельной механикой или ради дополнительных шансов на победу.',
          'Розыгрыш призов среди покупателей: автоматический по расписанию или ручной, с подтверждением списка победителей.',
          'Конкурс без чеков и кодов, когда покупка не нужна, — и конкурс поверх действующей чековой или кодовой акции.',
        ],
        itemsEn: [
          'Photo contests for shoppers: the participant uploads a shot with the product, the entry is moderated and joins the public gallery.',
          'Video contests and creative tasks — a drawing, a recipe, a story, a slogan.',
          'Contests with voting: open, where everyone votes, or closed, where a brand jury scores the entries.',
          'Social media contests that pull hashtag posts straight onto the campaign site.',
          'Quizzes, trivia and games — as a standalone mechanic or as a way to earn extra chances to win.',
          'Prize draws among shoppers: automatic on a schedule or manual, with the winner list confirmed by you.',
          'Contests with no receipts or codes, where no purchase is required — and contests layered on top of a running receipt or code campaign.',
        ],
      },
      {
        id: 'offering',
        title: 'Что входит в конкурс под ключ',
        titleEn: 'What a turnkey contest includes',
        kind: 'bullets',
        items: [
          'Форма загрузки работ, одинаково удобная с телефона и с компьютера.',
          'Регистрация участника по почте, номеру телефона, через социальные сети или мессенджер.',
          'Выделенная команда модераторов: работы смотрят люди, а не только автоматика.',
          'Голосование с защитой от накруток и таблица лидеров, которая обновляется на глазах.',
          'Автоматическое определение победителей и раздел с итогами на сайте акции.',
          'Призовой фонд любого вида: электронные сертификаты, деньги, физические подарки, накопительные баллы, кэшбэк.',
          'Персональная ссылка «позови друга» — вознаграждение получают оба, когда друг выполнит условие акции.',
          'Кнопка «поделиться»: за публикацию у себя на странице участник получает дополнительные шансы.',
          'Обработка обращений участников и онлайн-статистика с выгрузкой в Excel.',
        ],
        itemsEn: [
          'An upload form that works equally well on a phone and on a desktop.',
          'Sign-up by email, phone number, social network or messenger.',
          'A dedicated moderation team: entries are reviewed by people, not only by automation.',
          'Voting protected against manipulation, plus a live leaderboard.',
          'Automatic winner selection and a results section on the campaign site.',
          'A prize pool of any kind: e-certificates, cash, physical gifts, loyalty points, cashback.',
          'A personal "invite a friend" link — both sides are rewarded once the friend completes the required action.',
          'A share button: publishing on a personal page earns the participant extra chances.',
          'Participant support handling and live statistics with Excel export.',
        ],
      },
      {
        id: 'how-it-works',
        title: 'Как проходит конкурс — от брифа до вручения призов',
        titleEn: 'How a contest runs — from brief to prize handover',
        kind: 'numbered',
        items: [
          'Бриф: цель акции, сроки, призы, кто может участвовать и что считается работой.',
          'Собираем техническое задание, смету с зафиксированной ценой и план работ по датам.',
          'Рисуем и собираем сайт акции либо сценарий бота, подключаем нужные модули движка PromoKit.',
          'Проверяем готовое по чек-листу из 63 пунктов и открываем приём работ.',
          'Ведём модерацию, голосование и розыгрыши, отвечаем участникам всё время акции.',
          'Публикуем победителей, помогаем с вручением призов и отдаём выгрузки по итогам.',
        ],
        itemsEn: [
          'Brief: campaign goal, dates, prizes, who can enter and what counts as an entry.',
          'We prepare the technical specification, a fixed-price estimate and a dated work plan.',
          'We design and build the campaign site or the bot scenario and connect the required PromoKit modules.',
          'We run a 63-point quality checklist and open entry submissions.',
          'We handle moderation, voting and draws and answer participants throughout the campaign.',
          'We publish the winners, help with prize delivery and hand over the final data exports.',
        ],
      },
      {
        id: 'voting',
        title: 'Голосование и честный выбор победителя',
        titleEn: 'Voting and picking a winner fairly',
        kind: 'prose',
        paragraph:
          'Голосование можно открыть для всех — тогда работы выстраиваются по числу голосов, а участники сами приводят на сайт друзей и подписчиков, — либо оставить закрытым, когда оценки ставит жюри бренда по заранее описанным критериям. Чтобы результат нельзя было оспорить, голос привязывается к устройству и учётной записи: повторный с того же телефона не засчитывается. Так был устроен инструмент голосования, который мы делали для форума Fleet World: участники заходили по QR-коду в зале и могли проголосовать один раз. Если победителя определяет случай, розыгрыш проводит движок по заданной формуле, а результат сохраняется в системе — его видно в панели администратора и можно выгрузить.',
        paragraphEn:
          'Voting can be open to everyone — entries are then ranked by the number of votes and participants bring their own friends and followers to the site — or kept closed, with a brand jury scoring against criteria defined in advance. So that the outcome cannot be disputed, each vote is tied to a device and an account: a second vote from the same phone is not counted. That is how the voting tool we built for the Fleet World forum worked — attendees scanned a QR code in the hall and could vote once. Where chance decides the winner, the engine runs the draw by a preset formula and stores the result, visible in the admin panel and available for export.',
      },
      {
        id: 'moderation',
        title: 'Модерация работ и защита призового фонда',
        titleEn: 'Entry moderation and protecting the prize pool',
        kind: 'prose',
        paragraph:
          'Каждая присланная работа проходит проверку до публикации: модератор отсекает чужие фотографии, оскорбления, посторонние бренды и всё, что не подходит под правила акции. Там, где хорошие призы, всегда появляются призоловы — участники, которые заводят десятки учётных записей ради подарков. Мы узнаём их по цифровому отпечатку устройства и по поведению: частая смена браузера и адреса, слишком быстрая регистрация, повторные попытки входа. За такие сигналы начисляются баллы, и при достижении порога участник помечается как недобросовестный. Перед вручением дорогого приза победителя можно дополнительно сверить с нашей базой профессиональных призоловов, собранной за сотни запусков.',
        paragraphEn:
          'Every submission is reviewed before it goes public: the moderator filters out stolen photos, abuse, third-party brands and anything that does not fit the campaign rules. Wherever the prizes are good, prize hunters appear — people who create dozens of accounts to farm rewards. We recognise them by device fingerprint and by behaviour: frequent browser and IP changes, suspiciously fast sign-up, repeated login attempts. Each signal adds to a score, and past a threshold the participant is flagged. Before a valuable prize is handed over, the winner can also be checked against our database of professional prize hunters built up over hundreds of launches.',
      },
      {
        id: 'results',
        title: 'Публикация победителей и отчёт по акции',
        titleEn: 'Publishing winners and reporting on the campaign',
        kind: 'prose',
        paragraph:
          'Победители попадают в отдельный раздел сайта автоматически, с фильтром по типам розыгрышей и по неделям: участнику не нужно ждать, пока кто-то вручную обновит таблицу, а вам — каждую неделю собирать список руками. Денежные призы уходят на телефон или карту через подключённые платёжные системы, электронные сертификаты приходят ссылкой и файлом, физические подарки едут по адресам из личного кабинета. По ходу акции в панели видно, сколько работ загружено, сколько прошло модерацию и как распределяются голоса, а любые данные выгружаются в Excel — в том числе для отчёта юристам и торговой сети.',
        paragraphEn:
          'Winners land in a dedicated site section automatically, filtered by draw type and by week: participants do not wait for someone to update a table by hand, and you do not assemble the list manually every week. Cash prizes go to a phone number or a bank card through connected payment systems, e-certificates arrive as a link and a file, physical gifts ship to the addresses stored in the personal account. While the campaign runs, the dashboard shows how many entries were uploaded, how many passed moderation and how the votes are distributed, and any data can be exported to Excel — including for reports to legal teams and retail chains.',
      },
      {
        id: 'where',
        title: 'Где проводить конкурс: сайт, социальные сети или чат-бот',
        titleEn: 'Where to run a contest: site, social networks or chatbot',
        kind: 'prose',
        paragraph:
          'Площадку выбирают по тому, где живёт ваша аудитория. Отдельный сайт акции нужен, когда конкурс идёт вместе с чековой или кодовой механикой, призов много и участнику нужен личный кабинет. Чат-бот в Telegram или ВКонтакте дешевле и проще для входа: работу присылают прямо сообщением, ничего скачивать не надо. Социальные сети дают охват — публикации по хэштегу подтягиваются на сайт акции автоматически. По нашей статистике лучший результат даёт сочетание каналов: часть людей никогда не пойдёт на сайт, а часть не станет писать боту, и одна общая механика собирает и тех, и других.',
        paragraphEn:
          'The platform depends on where your audience already is. A separate campaign site makes sense when the contest runs alongside a receipt or code mechanic, when there are many prizes and the participant needs a personal account. A chatbot in Telegram or VK is cheaper and lowers the barrier: entries are sent straight as a message, with nothing to install. Social networks bring reach — hashtag posts are pulled onto the campaign site automatically. In our experience the best result comes from combining channels: some people will never open the site, others will never message a bot, and one shared mechanic collects both.',
      },
      {
        id: 'audience',
        title: 'Кому подходит',
        titleEn: 'Who it suits',
        kind: 'prose',
        paragraph:
          'Конкурс подходит брендам, которым нужен не только рост продаж, но и живой контент от покупателей — фотографии, видео и истории, которые потом можно использовать в коммуникации. Он хорошо работает, когда покупка не обязательна и важно расширить охват, когда у бренда есть сообщество, готовое участвовать, и когда обычная раздача призов аудиторию уже не удивляет. Конкурсную механику добавляют и к действующей акции с чеками: участник чаще возвращается и проводит на сайте больше времени, а бренд получает материал для социальных сетей.',
        paragraphEn:
          'Contests suit brands that want more than a sales bump — they want living content from shoppers: photos, videos and stories the brand can reuse in its own communication. They work especially well when no purchase is required and reach matters, when the brand already has a community willing to take part, and when plain prize giveaways no longer surprise the audience. A contest mechanic is often layered onto a running receipt campaign: participants return more often and spend longer on the site, and the brand collects material for social media.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Чем конкурс отличается от розыгрыша и лотереи?',
            answer:
              'В конкурсе победителя определяют по работе: её оценивает жюри или голосование участников. В розыгрыше выбор делает случай. От этого зависят правила акции — юридический документ, где участник должен найти ответ на любой вопрос, от списка акционных товаров до порядка получения приза. Формулировки обычно готовит юрист организатора, а мы собираем механику ровно по утверждённым правилам.',
          },
          {
            title: 'Как выбрать победителя честно, чтобы потом не было споров?',
            answer:
              'Опишите в правилах критерии оценки и способ выбора до старта. При голосовании один человек голосует один раз: голос привязан к устройству и учётной записи. При розыгрыше выбор делает движок по заданной формуле, результат фиксируется в системе и выгружается. Спорные работы снимает модератор до публикации, а не после объявления итогов.',
          },
          {
            title: 'Как публиковать итоги конкурса?',
            answer:
              'Победители автоматически попадают в раздел на сайте акции с фильтром по типам розыгрышей и неделям. Список обновляется сразу после подведения итогов, вручную вносить ничего не нужно. Тем же списком удобно отчитываться перед юристами и торговой сетью, а участники видят, что призы действительно уходят людям.',
          },
          {
            title: 'Нужна ли покупка, чтобы участвовать в конкурсе?',
            answer:
              'Не обязательно. Конкурс отлично работает без чеков и кодов — порог входа минимальный, охват шире. Если задача в росте продаж, конкурс ставят поверх чековой или кодовой механики: за загруженный чек участник получает право прислать работу или дополнительные шансы в розыгрыше.',
          },
          {
            title: 'Кто модерирует работы — мы или вы?',
            answer:
              'Как удобнее. У нас есть выделенная команда модераторов, которая смотрит присланное по вашим правилам. Можно работать и самим в интерфейсе модератора: там видно очередь работ, историю решений и причины отказа. Часто делят пополам — поток ведём мы, спорные случаи уходят на сторону бренда.',
          },
          {
            title: 'Сколько времени занимает запуск конкурса?',
            answer:
              'В среднем около трёх недель, срок зависит от сложности механики. Если берём стандартный шаблон и не придумываем новую механику, получается быстрее. Дольше всего в промо идёт не разработка, а согласования и правки макетов, поэтому план лучше строить от даты старта акции назад.',
          },
          {
            title: 'Как защититься от накруток голосов и призоловов?',
            answer:
              'Голос засчитывается один раз с устройства и учётной записи. Поведение участника оценивается баллами: смена браузера и адреса, слишком быстрая регистрация, десятки попыток входа. Учётные записи одного человека склеиваются в общий профиль по цифровому отпечатку устройства. Перед вручением дорогого приза победителя можно проверить по базе призоловов.',
          },
          {
            title: 'Можно ли провести конкурс внутри нашего контура?',
            answer:
              'Да. Платформа работает и в облаке, и на серверах компании, если требования безопасности не позволяют хранить данные снаружи. Механика связывается по программному интерфейсу с вашей CRM, картами лояльности и внутренними системами — менять их не нужно.',
          },
          {
            title: 'Сколько стоит проведение конкурса?',
            answer:
              'Цена складывается из подключённых модулей: приём работ, модерация, голосование, управление розыгрышами, выдача призов, рассылки. В калькуляторе на сайте можно собрать состав самому и получить смету в Excel с зафиксированной ценой, коммерческое предложение и план работ по датам.',
          },
        ],
        itemsEn: [
          {
            title: 'How is a contest different from a prize draw or a lottery?',
            answer:
              'In a contest the winner is chosen on the merit of the entry — scored by a jury or by participant voting. In a draw the winner is chosen by chance. The campaign rules follow from that choice: they are a legal document where a participant must find the answer to any question, from the list of eligible products to how a prize is collected. The wording is usually prepared by the organiser’s lawyer, and we build the mechanic exactly to the approved rules.',
          },
          {
            title: 'How do we pick a winner fairly and avoid disputes later?',
            answer:
              'Define the scoring criteria and the selection method in the rules before launch. With voting, one person votes once: the vote is tied to a device and an account. With a draw, the engine selects by a preset formula and the result is stored and exportable. Questionable entries are removed by moderators before publication, not after the results are announced.',
          },
          {
            title: 'How are contest results published?',
            answer:
              'Winners appear automatically in a dedicated section of the campaign site, filtered by draw type and by week. The list updates as soon as results are finalised, with no manual entry. The same list is convenient for reporting to legal teams and retail partners, and participants can see that prizes really do reach people.',
          },
          {
            title: 'Does a purchase have to be required to enter?',
            answer:
              'Not necessarily. A contest works well with no receipts or codes at all — the barrier is low and reach is wider. If the goal is sales growth, the contest sits on top of a receipt or code mechanic: uploading a valid receipt earns the right to submit an entry or extra chances in the draw.',
          },
          {
            title: 'Who moderates the entries — you or us?',
            answer:
              'Whichever suits you. We have a dedicated moderation team that reviews submissions against your rules. You can also work in the moderator interface yourself: it shows the entry queue, the decision history and rejection reasons. A common split is that we handle the flow and edge cases go to the brand.',
          },
          {
            title: 'How long does it take to launch a contest?',
            answer:
              'About three weeks on average, depending on the complexity of the mechanic. With a standard template and no newly invented mechanic it goes faster. The longest part of a promo is not development but approvals and design revisions, so plan backwards from the campaign start date.',
          },
          {
            title: 'How do you protect voting and the prize pool from abuse?',
            answer:
              'A vote counts once per device and account. Participant behaviour is scored: browser and IP switching, unusually fast sign-up, dozens of login attempts. Multiple accounts belonging to one person are merged into a single profile by device fingerprint. Before a valuable prize is awarded, the winner can be checked against the prize hunter database.',
          },
          {
            title: 'Can the contest run inside our own infrastructure?',
            answer:
              'Yes. The platform runs both in the cloud and on your company servers when security requirements rule out storing data outside. The mechanic connects over API to your CRM, loyalty cards and internal systems — you do not have to change them.',
          },
          {
            title: 'How much does running a contest cost?',
            answer:
              'The price is the sum of the modules you switch on: entry intake, moderation, voting, draw management, prize delivery, mailings. The calculator on this site lets you assemble the setup yourself and receive a fixed-price estimate in Excel, a commercial proposal and a dated work plan.',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы собрать волну работ от покупателей?',
      headingEn: 'Ready to spark a UGC wave around your brand?',
      lead: 'Покажем кейсы и предложим механику под ваши цели и бюджет.',
      leadEn: 'We will share cases and propose a mechanic that fits your goals and budget.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 5. Чат-бот                                                               */
  /* ------------------------------------------------------------------------ */
  {
    id: 'chatbot',
    seoTitle: 'Чат-бот для промо-акции',
    seoTitleEn: 'Chatbots for promo campaigns',
    heading: 'Чат-боты для промоакций в мессенджерах',
    headingEn: 'Chatbots for promotions inside messengers',
    lead: 'Промо в Telegram, ВКонтакте и WhatsApp: приём чеков и кодов, розыгрыши, игры и личный кабинет прямо в переписке с ботом.',
    leadEn:
      'Promotions inside Telegram, VK and WhatsApp: receipt and code entry, prize draws, games and a personal account right in the chat.',
    primaryCta: {
      label: 'Запустить чат-бота',
      labelEn: 'Launch a chatbot',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Обсудить проект',
      labelEn: 'Discuss the project',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'capabilities',
        title: 'Что умеет промо-бот',
        titleEn: 'What a promo bot can do',
        kind: 'bullets',
        items: [
          'Регистрация и вход прямо в переписке — по номеру телефона, почте или по данным с сайта акции.',
          'Приём чеков: участник присылает фотографию, бот распознаёт данные и сверяет их с ФНС.',
          'Приём кодов с упаковки и мгновенный ответ, засчитан код или уже использован.',
          'Приём конкурсных работ — фотографии, видео или текста.',
          'Розыгрыши по расписанию и моментальные призы прямо в чате.',
          'Облегчённый личный кабинет: мои чеки и коды, баллы, призы, обращения.',
          'Ответы на частые вопросы и приём обратной связи без участия оператора.',
          'Возрастное ограничение и правила акции первым сообщением.',
          'Рассылки: напомнить загрузить чек, позвать на розыгрыш, сообщить о выигрыше.',
          'Помощник на нейросети, который отвечает участникам своими словами.',
        ],
        itemsEn: [
          'Sign-up and login inside the chat — by phone number, email or the credentials from the campaign site.',
          'Receipt intake: the participant sends a photo, the bot reads the data and checks it against the tax service.',
          'Pack code entry with an instant answer on whether the code counted or was already used.',
          'Contest entry intake — a photo, a video or text.',
          'Scheduled prize draws and instant prizes right in the chat.',
          'A lightweight personal account: my receipts and codes, points, prizes, support requests.',
          'Answers to common questions and support intake without an operator.',
          'Age gate and campaign rules delivered in the first message.',
          'Broadcasts: remind people to upload a receipt, call them to a draw, announce a win.',
          'An AI assistant that answers participants in its own words.',
        ],
      },
      {
        id: 'messenger-choice',
        title: 'В каком мессенджере делать бота',
        titleEn: 'Which messenger to build the bot in',
        kind: 'prose',
        paragraph:
          'Доля людей, которые предпочитают загружать чеки в боте, а не на сайте, почти не меняется от мессенджера — важнее, во что вам обойдётся канал. Telegram и ВКонтакте удобнее всего: кнопки, быстрые сценарии, простой запуск, поэтому их обычно и берут для акций в центральном регионе. WhatsApp требует регистрации делового доступа, согласования исходящих сообщений и денег за ответ позже суток, а кнопок там нет — его стоит брать, когда акция нацелена на регионы, где им пользуются чаще. Viber остаётся экзотикой, но иногда закрывает именно свою аудиторию.',
        paragraphEn:
          'The share of people who prefer uploading receipts in a bot rather than on a site barely changes between messengers — what the channel costs you matters more. Telegram and VK are the most convenient: buttons, fast flows, an easy launch, which is why they are the usual pick for campaigns in central Russia. WhatsApp requires Business API registration, approval of outgoing messages and payment for replies sent after 24 hours, and it has no buttons — take it when the campaign targets regions where it dominates. Viber stays exotic but occasionally covers an audience nothing else reaches.',
      },
      {
        id: 'bot-or-site',
        title: 'Бот вместо сайта или вместе с сайтом',
        titleEn: 'A bot instead of a site, or alongside it',
        kind: 'prose',
        paragraph:
          'Если бот — единственный канал акции, он должен закрывать всю механику: регистрацию, приём и проверку чеков, призы, обратную связь. Если сайт уже есть, решите, насколько подробным делать бота: базовый вариант — вход и приём чеков, полный — почти копия личного кабинета. Часть аудитории участвует только в мессенджере и на сайт не пойдёт, поэтому связка «сайт плюс бот» по нашей статистике даёт результат лучше, чем любой канал по отдельности. Начинка при этом общая: участник, его чеки и баллы одни и те же, из какого бы канала он ни пришёл.',
        paragraphEn:
          'If the bot is the campaign’s only channel, it has to cover the whole mechanic: sign-up, receipt intake and validation, prizes, support. If a site already exists, decide how deep the bot should go: the basic version is login plus receipt intake, the full one is close to a copy of the personal account. Part of the audience takes part only in a messenger and will never open the site, so in our experience "site plus bot" beats either channel on its own. The engine underneath is shared: the same participant, receipts and points, whichever channel they arrive from.',
      },
      {
        id: 'receipts',
        title: 'Регистрация чеков и кодов в боте',
        titleEn: 'Receipt and code entry inside the bot',
        kind: 'prose',
        paragraph:
          'Участник фотографирует чек или сканирует QR-код прямо в переписке. Движок распознаёт данные, проверяет их через ФНС, находит в чеке акционные товары и определяет торговую точку — ответ приходит в чат за секунды. Чеки, которые автоматика забраковала, попадают на ручную проверку: разобрать их можно вашими силами или нашими. Для кодовых акций логика та же — код сверяется с базой сгенерированных кодов, и человек сразу видит результат. Настройки бывают строгими: например, принимать чеки только из выбранных городов или только за нужный период; всё, что не подходит, отклоняется автоматически.',
        paragraphEn:
          'The participant photographs the receipt or scans its QR code inside the chat. The engine reads the data, validates it with the tax service, finds the eligible products and identifies the retail outlet — the answer lands in the chat within seconds. Receipts rejected by automation go to manual review, handled by your team or ours. Code campaigns follow the same logic: the code is matched against the generated code base and the person sees the result at once. The rules can be strict — for example accepting receipts only from selected cities or only for a given period; anything else is declined automatically.',
      },
      {
        id: 'draw',
        title: 'Розыгрыш в телеграм-боте',
        titleEn: 'Running a prize draw in a Telegram bot',
        kind: 'prose',
        paragraph:
          'Призовой фонд заводится один раз — электронные сертификаты, деньги, физические подарки, накопительные баллы, кэшбэк, — а розыгрыши идут по расписанию: ежечасно, ежедневно, раз в неделю, раз в месяц или один главный в финале. Победителей движок определяет сам, а вы подтверждаете список перед вручением; при желании розыгрыш можно проводить вручную. Бот умеет писать первым, поэтому перед розыгрышем участникам уходит напоминание загрузить чек, а после — сообщение о результате. Итоги дублируются в разделе победителей на сайте акции, если сайт есть, и выгружаются таблицей.',
        paragraphEn:
          'The prize pool is set up once — e-certificates, cash, physical gifts, accumulated points, cashback — and draws run on a schedule: hourly, daily, weekly, monthly or a single grand draw at the end. The engine picks the winners and you confirm the list before handover; draws can also be run manually. The bot can message first, so participants get a reminder to upload a receipt before a draw and a result message afterwards. The outcome is mirrored in the winners section of the campaign site, if there is one, and exported as a table.',
      },
      {
        id: 'after',
        title: 'Что остаётся после акции',
        titleEn: 'What remains once the campaign ends',
        kind: 'prose',
        paragraph:
          'Люди, которые участвовали через бота, остаются с вами на связи — собрать такую базу заново заметно дороже. Рассылка в мессенджере проще и дешевле почтовой, особенно теперь, когда часть привычных почтовых сервисов с рынка ушла. После акции с этой аудиторией можно продолжать разговор: анонсировать следующий запуск, звать в сообщество, присылать полезное. Важно помнить: удалённый чат не означает отписку, сообщения будут приходить и дальше — поэтому пишите по делу и всегда оставляйте возможность отказаться от рассылки.',
        paragraphEn:
          'People who took part through the bot stay reachable — rebuilding that base from scratch costs noticeably more. Broadcasting in a messenger is simpler and cheaper than email, especially now that several familiar email services have left the market. Once the campaign is over you can keep the conversation going: announce the next launch, invite people into the community, send something useful. One caveat: deleting the chat is not unsubscribing, messages keep arriving — so write only when you have something to say and always leave a way to opt out.',
      },
      {
        id: 'how-we-build',
        title: 'Как мы запускаем бота',
        titleEn: 'How we build and launch the bot',
        kind: 'numbered',
        items: [
          'Бриф: механика акции, площадки, призы и то, что бот обязан уметь.',
          'Пишем сценарий переписки — каждый шаг участника, все ответы бота, тексты ошибок.',
          'Собираем бота и подключаем его к движку PromoKit: чеки, коды, призы, розыгрыши, обращения.',
          'Проверяем на живых чеках и правим формулировки до старта.',
          'Запускаем, читаем диалоги и дорабатываем сценарий по ходу акции.',
        ],
        itemsEn: [
          'Brief: the campaign mechanic, the platforms, the prizes and what the bot must be able to do.',
          'We write the conversation script — every participant step, every bot reply, every error message.',
          'We build the bot and wire it to the PromoKit engine: receipts, codes, prizes, draws, support.',
          'We test on real receipts and polish the wording before launch.',
          'We go live, read the conversations and refine the script while the campaign runs.',
        ],
      },
      {
        id: 'included',
        title: 'Что входит в базового бота, а что подключается отдельно',
        titleEn: 'What the basic bot includes and what is added on top',
        kind: 'bullets',
        items: [
          'В базовом наборе: вход по данным с сайта акции, регистрация по почте, приём чеков и кодов, правила акции.',
          'Отдельно подключаются приём конкурсных работ, личный кабинет с чеками, подарками и обращениями, раздел частых вопросов, обратная связь, возрастное ограничение.',
          'Площадки на выбор: Telegram и ВКонтакте, при необходимости WhatsApp.',
          'Игровые механики — викторина, квиз, простая казуальная игра — работают в боте так же, как на сайте.',
          'Диалоговая страница в браузере: тот же разговорный формат с кнопками, но мессенджер участнику не нужен.',
          'Связь с вашими системами по программному интерфейсу: CRM, карты лояльности, платёжные сервисы.',
        ],
        itemsEn: [
          'In the basic set: login with the campaign site credentials, email sign-up, receipt and code intake, campaign rules.',
          'Added separately: contest entry intake, a personal account with receipts, gifts and support requests, an FAQ section, feedback, an age gate.',
          'Platforms to choose from: Telegram and VK, plus WhatsApp where needed.',
          'Game mechanics — trivia, quizzes, a simple casual game — run in the bot exactly as they do on a site.',
          'A conversational page in the browser: the same chat-style flow with buttons, but no messenger required.',
          'API links to your systems: CRM, loyalty cards, payment services.',
        ],
      },
      {
        id: 'advantages',
        title: 'Почему чат-бот, а не ещё один лендинг',
        titleEn: 'Why a chatbot rather than another landing page',
        kind: 'bullets',
        items: [
          'Дешевле лендинга: не нужны дизайн и вёрстка отдельного сайта.',
          'Правку в сценарии или тексте делает один разработчик, обычно в тот же день.',
          'Ниже порог входа: участник уже в мессенджере, скачивать и регистрироваться отдельно не надо.',
          'Меньше нагрузка на поддержку — частые вопросы бот закрывает сам.',
          'Живая база контактов, с которой можно работать и после акции.',
          'Удобная площадка, чтобы проверить гипотезу или новую механику перед большим запуском.',
        ],
        itemsEn: [
          'Cheaper than a landing page: no separate site design and front-end build.',
          'A change to the script or the copy takes one developer, usually the same day.',
          'A lower barrier: the participant is already in the messenger, with nothing to install or register.',
          'Less load on support — the bot handles the common questions itself.',
          'A live contact base you can keep working with after the campaign.',
          'A convenient place to test a hypothesis or a new mechanic before a big launch.',
        ],
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Что бот умеет кроме приёма заявок?',
            answer:
              'Принимает и проверяет чеки через ФНС, засчитывает коды с упаковки, собирает конкурсные работы, показывает баллы и призы в облегчённом личном кабинете, проводит розыгрыши, отвечает на частые вопросы, принимает обращения и рассылает напоминания. Внутри бота работают викторины, квизы и простые игры.',
          },
          {
            title: 'В каком мессенджере делать бота для акции?',
            answer:
              'Для акций в центральном регионе обычно берут Telegram или ВКонтакте: там есть кнопки и запуск проще. WhatsApp требует делового доступа, согласования исходящих сообщений и платы за ответ позже суток, кнопок в нём нет — его выбирают под регионы. Доля участников, приходящих в бот, от мессенджера почти не зависит.',
          },
          {
            title: 'Бот может полностью заменить сайт акции?',
            answer:
              'Может, если это единственный канал и бот закрывает всю механику — регистрацию, чеки, призы, обратную связь. Но по нашей статистике сильнее работает связка: часть людей пойдёт только на сайт, часть — только в мессенджер. Механика у обоих каналов общая, данные не задваиваются.',
          },
          {
            title: 'Сколько занимает запуск чат-бота?',
            answer:
              'Обычно быстрее, чем страница, собранная с нуля. Точный срок зависит от того, сколько шагов в сценарии, нужен ли личный кабинет и придумываем ли мы новую механику. Мелкие правки текста и логики после старта делаются силами одного разработчика, обычно в течение дня.',
          },
          {
            title: 'Как бот проверяет чеки?',
            answer:
              'Участник присылает фотографию чека или сканирует QR-код. Данные распознаются и сверяются с ФНС, в чеке находятся акционные товары и определяется магазин. Ответ приходит в чат сразу. Чеки, забракованные автоматикой, уходят на ручную проверку — её ведёте вы или мы.',
          },
          {
            title: 'Можно ли провести розыгрыш прямо в телеграм-боте?',
            answer:
              'Да. Заводите призовой фонд, выбираете периодичность — от ежечасной до одного финального розыгрыша — и режим: автоматический или ручной. Победителей движок определяет сам, вы подтверждаете список перед вручением. Перед розыгрышем бот сам напомнит участникам загрузить чек.',
          },
          {
            title: 'Что будет с базой участников после акции?',
            answer:
              'База остаётся у вас: контакты, история участия, загруженные чеки. С этими людьми можно продолжать общение — рассылка в мессенджере дешевле почтовой. Учтите, что удалённый чат не равен отписке: сообщения будут приходить, поэтому оставляйте явную возможность отказаться.',
          },
          {
            title: 'Как бот защищён от призоловов?',
            answer:
              'Так же, как сайт: по цифровому отпечатку устройства и поведению участнику начисляются баллы подозрительности, учётные записи одного человека склеиваются в общий профиль, а перед вручением дорогого приза победителя можно сверить с базой профессиональных призоловов.',
          },
          {
            title: 'Сколько стоит чат-бот для промо?',
            answer:
              'Цена складывается из базового набора и дополнительных возможностей: конкурсные работы, личный кабинет, частые вопросы, обратная связь, возрастное ограничение, рассылки. Смету можно собрать в калькуляторе на сайте и получить её в Excel с зафиксированной ценой вместе с планом работ.',
          },
        ],
        itemsEn: [
          {
            title: 'What can the bot do besides collecting entries?',
            answer:
              'It accepts and validates receipts with the tax service, counts pack codes, collects contest entries, shows points and prizes in a lightweight personal account, runs draws, answers common questions, takes support requests and sends reminders. Trivia, quizzes and simple games run inside the bot too.',
          },
          {
            title: 'Which messenger should the campaign bot live in?',
            answer:
              'For campaigns in central Russia the usual choice is Telegram or VK: they have buttons and are simpler to launch. WhatsApp needs Business API access, approval of outgoing messages and payment for replies after 24 hours, and has no buttons — it is picked for regional targeting. The share of participants who use a bot barely depends on the messenger.',
          },
          {
            title: 'Can a bot fully replace the campaign site?',
            answer:
              'It can, if it is the only channel and covers the whole mechanic — sign-up, receipts, prizes, support. But in our experience the pairing works better: some people go only to the site, others only into the messenger. Both channels share one mechanic, so data is never duplicated.',
          },
          {
            title: 'How long does a chatbot take to launch?',
            answer:
              'On average about 30% faster than a landing page. The exact timing depends on how many steps the script has, whether a personal account is needed and whether we are inventing a new mechanic. Small copy and logic changes after launch take one developer, usually within a day.',
          },
          {
            title: 'How does the bot validate receipts?',
            answer:
              'The participant sends a photo of the receipt or scans its QR code. The data is recognised and checked with the tax service, eligible products are found and the store is identified. The answer arrives in the chat immediately. Receipts rejected by automation go to manual review, run by your team or ours.',
          },
          {
            title: 'Can a prize draw run inside a Telegram bot?',
            answer:
              'Yes. You set up the prize pool, choose the frequency — from hourly to a single grand draw — and the mode: automatic or manual. The engine selects the winners and you confirm the list before handover. Ahead of a draw the bot reminds participants to upload a receipt.',
          },
          {
            title: 'What happens to the participant base after the campaign?',
            answer:
              'The base stays with you: contacts, participation history, uploaded receipts. You can keep talking to these people — messenger broadcasts cost less than email. Note that deleting a chat is not unsubscribing: messages still arrive, so always give a clear way to opt out.',
          },
          {
            title: 'How is the bot protected against prize hunters?',
            answer:
              'The same way the site is: device fingerprint and behaviour add suspicion points, multiple accounts belonging to one person are merged into a single profile, and before a valuable prize is awarded the winner can be checked against the professional prize hunter database.',
          },
          {
            title: 'How much does a promo chatbot cost?',
            answer:
              'The price combines the basic set with the extras you switch on: contest entries, a personal account, an FAQ, feedback, an age gate, broadcasts. You can assemble the estimate in the site calculator and receive it in Excel at a fixed price together with the work plan.',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы запустить промо в мессенджерах?',
      headingEn: 'Ready to launch a promo inside messengers?',
      lead: 'Спроектируем сценарий бота и подключим его к движку Promokit.',
      leadEn: 'We will design the bot scenario and wire it up to the Promokit engine.',
    },
  },

  /* ------------------------------------------------------------------------ */
  /* 6. Сайт                                                                  */
  /* ------------------------------------------------------------------------ */
  {
    id: 'site',
    seoTitle: 'Промо-сайт акции под ключ',
    seoTitleEn: 'Turnkey promo campaign site',
    heading: 'Промо-сайт акции под ключ: личный кабинет, разделы и панель администратора',
    headingEn: 'Turnkey campaign site: participant account, sections and an admin panel',
    lead: 'Полноценный сайт акции: разделы, личный кабинет участника, панель администратора и движок, который считает чеки, коды, баллы и розыгрыши.',
    leadEn:
      'A full campaign site: sections, a participant account, an admin panel and an engine that handles receipts, codes, points and prize draws.',
    primaryCta: {
      label: 'Обсудить сайт',
      labelEn: 'Discuss your site',
      target: 'brief',
    },
    secondaryCta: {
      label: 'Связаться с командой',
      labelEn: 'Talk to the team',
      target: 'home-contact',
    },
    sections: [
      {
        id: 'what-is',
        title: 'Промо-сайт акции — это не одна страница, а проект целиком',
        titleEn: 'A promo site is not one page but the whole project',
        kind: 'prose',
        paragraph:
          'У участника есть личный кабинет: какие чеки прошли проверку, сколько накопил баллов, какие призы уже его и что с обращением. У бренда — панель администратора со статистикой, розыгрышами и выгрузками. Между ними работает движок: проверяет чеки и коды, считает баллы, проводит розыгрыши по заданной формуле. Такой формат берут, когда акция идёт долго, механик несколько и людям есть зачем возвращаться на сайт.',
        paragraphEn:
          'The participant gets a personal account: which receipts passed validation, how many points are collected, which prizes are already theirs and what happened to their request. The brand gets an admin panel with statistics, prize draws and exports. Between them runs the engine: it validates receipts and codes, counts points and runs draws by a defined formula. This format fits campaigns that run long, hold several mechanics and give people a reason to come back.',
      },
      {
        id: 'structure',
        title: 'Из каких разделов состоит сайт для промо-акции',
        titleEn: 'Which sections a promo campaign site has',
        kind: 'bullets',
        items: [
          'Главная с призами, условиями и шагами участия.',
          'Правила акции и частые вопросы.',
          'Раздел победителей — наполняется сам, с фильтрами по неделям, турам и типам розыгрышей.',
          'Регистрация и вход: почта, СМС, соцсети, мессенджеры.',
          'Обратная связь: форма, из которой обращения попадают в интерфейс обработки.',
        ],
        itemsEn: [
          'A home page with prizes, conditions and the steps to take part.',
          'Campaign rules and frequently asked questions.',
          'A winners section that fills itself, with filters by weeks, rounds and draw types.',
          'Sign-up and login: email, SMS, social networks, messengers.',
          'Feedback: a form whose requests land in the processing interface.',
        ],
      },
      {
        id: 'cabinet',
        title: 'Что участник делает в личном кабинете',
        titleEn: 'What the participant does in their account',
        kind: 'bullets',
        items: [
          'Загружает чеки или коды и видит статус каждого: принят, на проверке, отклонён.',
          'Копит баллы и следит за прогрессом до нужной категории призов.',
          'Выбирает приз из каталога и запрашивает его.',
          'Получает электронный приз — сертификат или ссылку — и следит за отправкой физического.',
          'Зовёт друга по личной ссылке и получает за это вознаграждение.',
        ],
        itemsEn: [
          'Uploads receipts or codes and sees the status of each: accepted, in review, rejected.',
          'Collects points and tracks progress towards the prize tier they want.',
          'Picks a prize from the catalogue and requests it.',
          'Receives a digital prize — a certificate or a link — and follows the shipping of a physical one.',
          'Invites a friend by a personal link and is rewarded for it.',
        ],
      },
      {
        id: 'admin',
        title: 'Что делает ваша команда в панели администратора',
        titleEn: 'What your team does in the admin panel',
        kind: 'bullets',
        items: [
          'Смотрит показатели акции в реальном времени и выгружает участников, чеки и розыгрыши в Excel.',
          'Ведёт призовой фонд: цифровые, денежные, физические, накопительные призы и кэшбэк.',
          'Запускает розыгрыши по расписанию или вручную и подтверждает победителей перед публикацией.',
          'Разбирает обращения участников — с разграничением прав по ролям.',
          'Настраивает цели и смотрит метрики аналитики, не уходя в чужой интерфейс.',
        ],
        itemsEn: [
          'Watches live campaign figures and exports participants, receipts and draws to Excel.',
          'Runs the prize pool: digital, cash, physical and cumulative prizes plus cashback.',
          'Launches draws on schedule or manually and confirms winners before publication.',
          'Handles participant requests, with access split by roles.',
          'Sets goals and reads analytics counters without leaving for another interface.',
        ],
      },
      {
        id: 'engine',
        title: 'Движок акции: подключаем только нужные модули',
        titleEn: 'The campaign engine: only the modules you need',
        kind: 'bullets',
        items: [
          'Проверка чеков через ФНС с распознаванием товаров бренда и торговой сети, готовые интеграции с ОФД.',
          'Генерация и проверка кодов — от одного до нескольких миллионов.',
          'Розыгрыши, накопительная система и выдача призов, включая моментальные выплаты на телефон или карту.',
          'Письма и СМС участникам: регистрация, восстановление доступа, статусы, ответы на обращения.',
          'Кнопка «Поделиться», уведомления в браузере, конкурсы, квизы и игры.',
          'Платить сразу за всё не нужно: набор модулей собирается под механику вашей акции.',
        ],
        itemsEn: [
          'Receipt validation through the tax service, recognising brand products and the retail chain, with ready integrations with fiscal data operators.',
          'Code generation and validation — from one code to several million.',
          'Prize draws, a points system and prize delivery, including instant payouts to a phone number or a card.',
          'Emails and SMS to participants: sign-up, access recovery, statuses, replies to requests.',
          'A share button, browser notifications, contests, quizzes and games.',
          'There is no need to pay for everything at once: the set of modules is assembled around your mechanic.',
        ],
      },
      {
        id: 'workflow',
        title: 'Шесть этапов от брифа до закрытия акции',
        titleEn: 'Six stages from brief to campaign close',
        kind: 'numbered',
        items: [
          'Собираем информацию и пишем техническое задание — с ним можно идти на тендер. Плюс смета, план работ и путь участника.',
          'Рисуем прототип и уникальный дизайн сайта акции.',
          'Собираем проект в конструкторе на готовых блоках: это экономит до 60% времени разработки.',
          'Разрабатываем и проверяем: штатные разработчики, контрольный чек-лист из 63 пунктов.',
          'Подключаем движок с нужными модулями, интеграции с вашими системами и доступ администратора к панели.',
          'Ведём акцию: круглосуточный мониторинг сайта и механик, правки по ходу, выделенный менеджер.',
        ],
        itemsEn: [
          'We gather the information and write the technical specification — the kind you can take to a tender. Plus the estimate, the work plan and the participant journey.',
          'We draw the prototype and a unique design for the campaign site.',
          'We assemble the project in the builder on ready-made blocks: that saves up to 60% of development time.',
          'We develop and check it — a 63-point quality checklist, in-house developers with over seven years of experience.',
          'We connect the engine with the modules you need, the integrations with your systems and admin access to the panel.',
          'We run the campaign: round-the-clock monitoring of the site and the mechanics, edits on the go, a dedicated manager.',
        ],
      },
      {
        id: 'cooperation',
        title: 'Три способа получить промо-сайт',
        titleEn: 'Three ways to get a promo site',
        kind: 'bullets',
        items: [
          'Под ключ: делаем всё — от технического задания до сопровождения на всём сроке акции.',
          'Своими силами: вы получаете доступ к конструктору и собираете акцию сами, мы помогаем с настройками.',
          'Развитие того, что есть: переносим существующий сайт акции или бренда на платформу.',
        ],
        itemsEn: [
          'Turnkey: we do everything from the specification to support for the whole campaign.',
          'On your own: you get access to the builder and assemble the campaign yourselves while we help with settings.',
          'Growing what exists: we rework your current campaign or brand site and move it onto the platform.',
        ],
      },
      {
        id: 'deployment',
        title: 'Домен, серверы и интеграции',
        titleEn: 'Domain, servers and integrations',
        kind: 'prose',
        paragraph:
          'Менять свои системы не нужно. Сайт подключается к вашему домену записями DNS или кодом, а платформа работает и в облаке, и на внутренних серверах компании — так обычно делают корпорации с требованиями к хранению данных. Дальше — связь по API с вашей CRM, ERP, корпоративной шиной, платёжными сервисами и картами лояльности.',
        paragraphEn:
          'You do not have to change your systems. The site is connected to your domain by DNS records or by code, and the platform runs both in the cloud and on the company internal servers — the latter is usually chosen by corporations with data storage requirements. From there the platform talks over API to your CRM, ERP, corporate data bus, payment services and loyalty cards.',
      },
      {
        id: 'audience',
        title: 'Кому это нужно',
        titleEn: 'Who needs this',
        kind: 'prose',
        paragraph:
          'Производителям продуктов питания и товаров повседневного спроса, торговым сетям и агентствам, которые ведут акцию месяцами: с несколькими турами розыгрышей, накоплением баллов и живой обратной связью. За восемь лет мы сделали больше 400 промо-сайтов и провели больше 220 акций со сложной механикой.',
        paragraphEn:
          'Food producers and makers of everyday consumer goods, retail chains and agencies that run a campaign for weeks or months: several draw rounds, points collection and live participant support. In eight years we have built over 400 promo sites and run over 220 campaigns with complex mechanics.',
      },
      {
        id: 'faq',
        title: 'Частые вопросы',
        titleEn: 'FAQ',
        kind: 'faq',
        items: [
          {
            title: 'Чем промо-сайт отличается от промо-лендинга?',
            answer:
              'Лендинг — одна страница под быстрый старт. Промо-сайт — проект акции: несколько разделов, личный кабинет, где участник видит статусы чеков и баллы, и панель администратора, где ваша команда ведёт розыгрыши, победителей и обратную связь.',
          },
          {
            title: 'Сколько занимает разработка промо-сайта?',
            answer:
              'В среднем около четырёх недель, точнее — после согласования технического задания. Ускоряет дело библиотека готовых блоков: с ней разработка идёт примерно вчетверо быстрее, чем сборка такого же проекта с нуля, и обходится дешевле.',
          },
          {
            title: 'Можем ли мы собрать сайт сами?',
            answer:
              'Да, это один из вариантов: вы получаете доступ к конструктору и собираете акцию сами. Команде обычно хватает короткого вводного курса по интерфейсу, а техподдержка отвечает на вопросы по настройкам. Дизайн можно заказать у нас или сделать самим.',
          },
          {
            title: 'Где будут храниться данные участников?',
            answer:
              'Есть два варианта. Для среднего бизнеса — на наших серверах: сайт подключается к домену записями DNS, данные шифруются. Для корпораций платформа ставится на ваши серверы, и обработка данных не выходит за периметр компании.',
          },
          {
            title: 'С какими нашими системами вы соединитесь?',
            answer:
              'Платформа связывается по API с CRM и ERP, корпоративными шинами, платёжными системами и картами лояльности. Для чековых акций готовы интеграции с ОФД и доступ к API ФНС — разрабатывать отдельно не нужно.',
          },
          {
            title: 'Кто обрабатывает обратную связь и спорные чеки?',
            answer:
              'Как договоримся. Можем открыть вашей команде интерфейс обработки обращений с ролями и правами, а можем взять обработку на себя — тогда доступ к интерфейсу не оплачивается. Ручная проверка части чеков подключается опцией.',
          },
          {
            title: 'Как защищаетесь от призоловов и мультиаккаунтов?',
            answer:
              'За сотни запусков у нас накопилась база профессиональных призоловов, победителя можно проверить по ней до вручения приза. Количеством полей в форме это не решается: опытные участники их обходят, смотреть надо на поведение.',
          },
          {
            title: 'Можно ли менять сайт и настройки во время акции?',
            answer:
              'Да. Периоды и туры включаются и скрываются, блок победителей перенастраивается, контент и правила обновляются на ходу. Работоспособность сайта и механик поддерживаем весь срок акции, а доработки считаем отдельно и заранее согласовываем смету.',
          },
          {
            title: 'Что происходит с сайтом после окончания акции?',
            answer:
              'Акция закрывается ступенями: сначала перестают приниматься чеки, потом заканчивается их проверка, выдаются призы и разбирается обратная связь. Всё это время участники заходят в кабинет и смотрят результаты. Потом появляется сообщение о завершении.',
          },
        ],
        itemsEn: [
          {
            title: 'How is a promo site different from a promo landing?',
            answer:
              'A landing is one page for a fast start. A promo site is the campaign project: several sections, a personal account where the participant sees receipt statuses and points, and an admin panel where your team runs draws, winners and support.',
          },
          {
            title: 'How long does a promo site take to build?',
            answer:
              'About four weeks on average, and more precisely once the specification is agreed. The library of ready blocks and templates speeds things up: development runs roughly four times faster than assembling the same project from scratch.',
          },
          {
            title: 'Can we build the site ourselves?',
            answer:
              'Yes, that is one of the options: you get access to the builder and assemble the campaign yourselves. A short introduction to the interface is usually enough for the team, and support answers questions about settings. The design can be ordered from us or made in-house.',
          },
          {
            title: 'Where will participant data be stored?',
            answer:
              'There are two options. For mid-sized business, on our servers: the site is connected to your domain by DNS records and the data is encrypted. For corporations the platform is installed on your servers and data processing never leaves the company perimeter.',
          },
          {
            title: 'Which of our systems will you connect to?',
            answer:
              'The platform talks over API to CRM and ERP systems, corporate data buses, payment systems and loyalty cards. For receipt campaigns, integrations with fiscal data operators and access to the tax service API are already in place.',
          },
          {
            title: 'Who handles support requests and disputed receipts?',
            answer:
              'Whichever way we agree. We can open the request-handling interface to your team with roles and permissions, or take the handling on ourselves — in that case access to the interface is not charged separately. Manual checking of part of the receipts is an option.',
          },
          {
            title: 'How do you protect the campaign from prize hunters and multi-accounts?',
            answer:
              'Across hundreds of launches we have built up a database of professional prize hunters, and a winner can be checked against it before the prize is handed over. Form length does not solve the problem: experienced players get around it, so we look at data and behaviour.',
          },
          {
            title: 'Can the site and its settings be changed during the campaign?',
            answer:
              'Yes. Periods and rounds are switched on and hidden, the winners block is reconfigured, content and rules are updated on the go. We keep the site and the mechanics running for the whole campaign; new functionality is quoted separately.',
          },
          {
            title: 'What happens to the site after the campaign ends?',
            answer:
              'The campaign closes in steps: receipts stop being accepted, then validation finishes, prizes are issued and support requests are closed. All that time registered participants can log in and check results. After that a closing notice appears on the site.',
          },
        ],
      },
    ],
    closing: {
      heading: 'Готовы обсудить ваш сайт?',
      headingEn: 'Ready to talk about your site?',
      lead: 'Подберём формат — конструктор, кастом или редизайн — и составим план работ.',
      leadEn:
        'We will pick the format — builder, custom or redesign — and put together a work plan.',
    },
  },
];

/** Карта slug → SubproductItem (для O(1)-доступа из роутера и страницы). */
export const SUBPRODUCT_BY_ID: Readonly<Record<string, SubproductItem>> = Object.freeze(
  Object.fromEntries(SUBPRODUCTS.map((s) => [s.id, s])),
);

export const SUBPRODUCT_IDS: readonly string[] = SUBPRODUCTS.map((s) => s.id);

/** Type guard — пригодится в роутер-гарде и страничном lookup. */
export function isSubproductId(value: string): boolean {
  return Object.prototype.hasOwnProperty.call(SUBPRODUCT_BY_ID, value);
}
